/* kr-poster-maker.js — the city poster maker on a product page.

   A map the customer frames on the poster itself, then the colors, the
   layout, the words, a marker, the size and finish, and Add to cart. Every
   pixel of the poster is drawn by kr-poster-style.js (window.KRPoster): the
   map through its MapLibre style, the frame/fade/words through drawOverlay —
   the same calls the print file makes, so the preview IS the print, only
   smaller.

   The editor (2026-10-24 redesign, drawn on the "City Poster Maker Editor"
   design canvas): a bar with what is being made, Share, the price and Add
   to cart; six steps on a rail (Place, Style, Layout, Words, Marker, Size &
   finish), each step's panel ending in Back / Next; the poster on a wall
   with its finish drawn around it — an oak frame's four mitred rails with
   grain and the lip's shadow on the print, a canvas's weave, a mounted
   board's edge. On a phone the poster is pinned under the site header with
   the steps as a strip under it, and a buy bar is pinned to the bottom.

   Mount: sections/kr-poster-maker.liquid renders <div data-kpm> plus a JSON
   config script (product, money format, tiles URL, geocoder key, the place it
   opens on), then this file and kr-poster-style.js as soon as they arrive.
   The map libraries (MapLibre, the PMTiles reader) load beside them, async:
   the editor is up and working before they land, and the map starts once
   both are on the page (needLibs, startMap). Waiting for MapLibre (~270 KB)
   kept a phone on a slow link looking at an empty page for ~12 s
   (2026-10-26).

   A link can name what the maker opens on, in its query, when it carries no
   saved design: ?color=blue (a color set's key) and, for a place,
   ?city=Paris&sub=France&lat=48.85&lon=2.35&zoom=12. The home page's color
   thumbs link that way. A saved design (#d=) always wins, and the first
   save folds the opening into the hash and drops it from the query, so a
   shared link says one thing (2026-10-25).

   A WHOLE STATE (2026-10-25, the "State Road Posters" design): the Place
   step's door "A whole state" offers the fifty states. Every road in the
   state is drawn at the state ladder from a pre-rendered class layer per
   state, orientation and line class, all in ONE PMTiles archive the worker
   serves like the map tiles (docs/poster-maker/state/), recolored here into
   the design's colours — the planet
   tiles carry no small roads at a state's scale, so there is no live map;
   the poster's words and rules are drawn as for a city. The state is cut to
   its outline on the paper (the silhouette form, Kevin's pick; no fade band).
   The state's name and UNITED STATES are the words, its centroid the
   coordinates line, its shape suggests the orientation; the Marker step
   steps aside (a state has no map to point at). d.state, d.share and
   d.ladder ride in the design, so the order's print link says exactly what
   was seen.

   LOCAL LETTERING (2026-10-26, Kevin: "for places like Tokyo or applicable
   countries can we have a choice to display the lettering to Japanese"):
   a place in a country that writes in a script of its own (Japan, Korea,
   China, Taiwan, Hong Kong, Greece, Russia, Ukraine, Bulgaria, Serbia,
   Thailand, Cambodia, Laos, Myanmar, the Arabic-speaking countries,
   Israel — KRPoster.LOCAL) gets a sixth card in the Words step's
   Lettering: its face the place's own name (東京), its name the language.
   Picking it sets the title and second line in the place's own spelling
   (東京 / 日本) in a Noto face for the script; a Latin card puts the
   English back. Only words that are still the place's own are swapped —
   what the customer typed stays as typed. The own spelling comes from the
   geocoder in the place's language (one more place-details call, only for
   such a place), the country's from the browser's locale data; d.local
   keeps both spellings so a saved link switches both ways.

   The design rides into the cart as line-item properties: readable lines for
   the customer (place, words, colors, size) plus `_design` (the whole design
   as a short string) and `_print_file` (this page with ?print=1, the variant
   and the design in the hash). Kevin pastes that link into the print tool on
   his PC (docs/poster-maker/print/), which makes the 600 DPI file: the live
   page has no print renderer, so no visitor can make one (2026-10-24). */
(function (root) {
  'use strict';
  var doc = root.document;
  if (!doc || root.KRPosterMaker) return;

  /* a city frame opens at this zoom (design units; the map is ~1200 units
     across on a 13x19), other place kinds step in from it */
  var Z_CITY = 11.6, Z_TOWN = 12.6, Z_HOOD = 13.6, Z_STREET = 14.8, Z_REGION = 9;
  /* tag = the short word on the place's chip; a place in a country with a
     script of its own names its country (cc) and its own spelling (own),
     for the local lettering (KRPoster.LOCAL) */
  var POPULAR = [
    { title: 'New York', sub: 'United States', tag: 'NY', lat: 40.7313, lon: -74.0, zoom: 11.0 },
    { title: 'Paris', sub: 'France', tag: 'France', lat: 48.8566, lon: 2.3522, zoom: 12.0 },
    { title: 'London', sub: 'United Kingdom', tag: 'UK', lat: 51.5074, lon: -0.1278, zoom: 11.6 },
    { title: 'Chicago', sub: 'United States', tag: 'IL', lat: 41.8781, lon: -87.6298, zoom: 11.7 },
    { title: 'Denver', sub: 'Colorado', tag: 'CO', lat: 39.7392, lon: -104.9903, zoom: 11.9 },
    { title: 'Fort Collins', sub: 'Colorado', tag: 'CO', lat: 40.5853, lon: -105.0844, zoom: 12.5 },
    { title: 'San Francisco', sub: 'California', tag: 'CA', lat: 37.7749, lon: -122.4194, zoom: 12.2 },
    { title: 'Las Vegas', sub: 'Nevada', tag: 'NV', lat: 36.1699, lon: -115.1398, zoom: 11.8 },
    { title: 'Minneapolis', sub: 'Minnesota', tag: 'MN', lat: 44.9778, lon: -93.265, zoom: 12.0 },
    { title: 'Prague', sub: 'Czech Republic', tag: 'Czechia', lat: 50.0755, lon: 14.4378, zoom: 12.6 },
    { title: 'Rome', sub: 'Italy', tag: 'Italy', lat: 41.9028, lon: 12.4964, zoom: 12.4 },
    /* aimed at the Imperial Palace (Kevin, 2026-10-26), not the usual Tokyo
       point out in Suginami, which left the palace, Ginza and the bay at the
       poster's right edge */
    { title: 'Tokyo', sub: 'Japan', tag: 'Japan', lat: 35.6852, lon: 139.7528, zoom: 11.6, cc: 'JP', own: '東京' }
  ];
  /* line icons, 24-unit boxes */
  var I = {
    place: '<path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0C18.5 15.4 12 21 12 21z"/><circle cx="12" cy="10" r="2.4"/>',
    style: '<path d="M12 3.5c4.7 0 8.5 3.4 8.5 7.6 0 2.4-1.9 3.6-3.6 3.6h-1.6c-1 0-1.6.7-1.6 1.5 0 .5.2.8.4 1.2.3.5.5.9.5 1.5 0 1.3-1.1 2.1-2.6 2.1-4.7 0-8.5-3.8-8.5-8.5S7.3 3.5 12 3.5z"/><circle cx="8" cy="10.5" r="1.1"/><circle cx="11" cy="7.3" r="1.1"/><circle cx="15.3" cy="8.3" r="1.1"/>',
    layout: '<rect x="4.5" y="3.5" width="15" height="17" rx="1.5"/><path d="M4.5 14.5h15M8 17.5h8"/>',
    words: '<path d="M5 6.5V5h14v1.5M12 5v14M9 19h6"/>',
    marker: '<path d="M12 20.5c-1-.9-6.6-5.7-7.7-8.3C3.2 9.5 4.7 6.4 7.7 6.2c1.9-.2 3.3.8 4.3 2.3 1-1.5 2.4-2.5 4.3-2.3 3 .2 4.5 3.3 3.4 6-1.1 2.6-6.7 7.4-7.7 8.3z"/>',
    size: '<rect x="3.5" y="3.5" width="17" height="17" rx="1"/><rect x="7" y="7" width="10" height="10"/>',
    state: '<path d="M4.5 5.5h9l1.5 2.5h4.5v8.5l-3 2.5h-6l-1.5-1.5h-4.5z"/>',
    search: '<circle cx="11" cy="11" r="6.5"/><path d="M16 16l4.5 4.5"/>',
    check: '<path d="M5 12.4l4.2 4.2L19 6.8"/>',
    share: '<path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7"/><path d="M12 15V3M7.5 7.5L12 3l4.5 4.5"/>',
    left: '<path d="M15 6l-6 6 6 6"/>',
    right: '<path d="M9 6l6 6-6 6"/>',
    plus: '<path d="M12 5v14M5 12h14"/>',
    minus: '<path d="M5 12h14"/>',
    reset: '<path d="M9 14L4 9l5-5"/><path d="M4 9h10a6 6 0 0 1 0 12h-3"/>',
    lock: '<rect x="5" y="10.5" width="14" height="10" rx="2"/><path d="M8.5 10.5V7.5a3.5 3.5 0 0 1 7 0v3"/>',
    unlock: '<rect x="5" y="10.5" width="14" height="10" rx="2"/><path d="M8.5 10.5V7.5a3.5 3.5 0 0 1 6.8-1.2"/>',
    whole: '<path d="M9 4.5H4.5V9M15 4.5h4.5V9M9 19.5H4.5V15M15 19.5h4.5V15"/>',
    hand: '<path d="M8 13V5.5a1.5 1.5 0 0 1 3 0V12M11 11.5V9.5a1.5 1.5 0 0 1 3 0V12M14 11.5V10.5a1.5 1.5 0 0 1 3 0V13M17 12.5a1.5 1.5 0 0 1 3 0v3.5a6 6 0 0 1-6 6h-2.5a6 6 0 0 1-5-2.7L4 15.5a1.6 1.6 0 0 1 2.6-1.8L8 15.5"/>',
    portrait: '<rect x="6.5" y="3.5" width="11" height="17" rx="1"/>',
    landscape: '<rect x="3.5" y="6.5" width="17" height="11" rx="1"/>',
    poster: '<rect x="5.5" y="3.5" width="13" height="17" rx="0.5"/><path d="M5.5 15l4-3.5 3 2.5 2.5-2 3.5 3"/>',
    framed: '<rect x="3.5" y="3.5" width="17" height="17"/><rect x="7.5" y="7.5" width="9" height="9"/>',
    canvas: '<rect x="4" y="5" width="15" height="13"/><path d="M19 5l2 2v13H6l-2-2"/>'
  };
  var STEPS = [
    { id: 'place', label: 'Place' },
    { id: 'style', label: 'Style' },
    { id: 'layout', label: 'Layout' },
    { id: 'words', label: 'Words' },
    { id: 'marker', label: 'Marker' },
    { id: 'size', label: 'Size' }
  ];
  /* A PHONE HAS FOUR STEPS (Kevin, 2026-10-27: "you scroll the page rather
     than the drawer… as well as combine some of the options", after
     Mapiful's phone editor). The six panes stay; a phone step shows its
     panes one under the other: the marker under the place (both are about
     the map), the layouts under the colors. Each phone step is named for
     its first pane, so data-tab, the CSS and the thumbnails' visible()
     keep the desktop ids. Desktop keeps its six. */
  var PHONE_STEPS = [
    { id: 'place', label: 'Place', panes: ['place', 'marker'] },
    { id: 'style', label: 'Look', panes: ['style', 'layout'] },
    { id: 'words', label: 'Words', panes: ['words'] },
    { id: 'size', label: 'Size', panes: ['size'] }
  ];
  function phoneStepOf(id) {
    for (var i = 0; i < PHONE_STEPS.length; i++) if (PHONE_STEPS[i].panes.indexOf(id) >= 0) return PHONE_STEPS[i];
    return PHONE_STEPS[0];
  }
  /* the Size step asks for the format first; a variant's format is read off
     its finish ("Style") value — see formatOf */
  var FORMATS = [{ key: 'poster', label: 'Poster' }, { key: 'framed', label: 'Framed' }, { key: 'canvas', label: 'Canvas' }];
  /* the frame picker's swatch colors (the mouldings themselves: the CSS) */
  var WOOD = { whiteoak: '#d3aa70', darkoak: '#5b3418', natural: '#c7a072', walnut: '#5b3f2c', black: '#232323', white: '#f3f1ec' };
  /* painted frames show almost no grain (the CSS sets the same per frame) */
  var GRAIN = { black: 0.12, white: 0.06 };
  /* style and layout cards: two across (~170 CSS px), drawn at twice that */
  var THUMB_W = 340;
  var TICK = '<span class="kpm-tick" aria-hidden="true"><svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">' + I.check + '</svg></span>';
  var uidN = 0;

  function h(tag, attrs, html) {
    var e = doc.createElement(tag);
    if (attrs) for (var k in attrs) {
      if (k === 'class') e.className = attrs[k];
      else if (attrs[k] != null && attrs[k] !== false) e.setAttribute(k, attrs[k] === true ? '' : attrs[k]);
    }
    if (html != null) e.innerHTML = html;
    return e;
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function debounce(fn, ms) {
    var t; return function () { var a = arguments, s = this; clearTimeout(t); t = setTimeout(function () { fn.apply(s, a); }, ms); };
  }
  function uniq(list) { return list.filter(function (v, i) { return v != null && list.indexOf(v) === i; }); }
  function money(cents, fmt) {
    fmt = fmt || '${{amount}}';
    var v = (cents || 0) / 100;
    function grp(n, dec, th, ds) {
      var p = n.toFixed(dec).split('.');
      p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, th);
      return p.join(ds);
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (m, k) {
      if (k === 'amount_no_decimals') return grp(v, 0, ',', '.');
      if (k === 'amount_with_comma_separator') return grp(v, 2, '.', ',');
      if (k === 'amount_no_decimals_with_comma_separator') return grp(v, 0, '.', ',');
      return grp(v, 2, ',', '.');
    });
  }
  /* the print file's pixels-per-inch, as a PNG pHYs chunk after IHDR. A
     canvas PNG carries none, so Photoshop and print drivers read 72 DPI and
     a 13x19 at 600 DPI opened as 108 x 158 inches. */
  var CRC_TABLE = null;
  function crc32(bytes) {
    if (!CRC_TABLE) {
      CRC_TABLE = [];
      for (var n = 0; n < 256; n++) {
        var c = n;
        for (var k = 0; k < 8; k++) c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
        CRC_TABLE[n] = c >>> 0;
      }
    }
    var crc = 0xffffffff;
    for (var i = 0; i < bytes.length; i++) crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
    return (crc ^ 0xffffffff) >>> 0;
  }
  function pngWithDpi(blob, dpi) {
    return blob.arrayBuffer().then(function (buf) {
      var src = new Uint8Array(buf), view = new DataView(buf);
      /* leave it alone if it isn't a PNG or already has a pHYs */
      if (src.length < 33 || src[1] !== 0x50) return blob;
      for (var at = 8; at + 8 <= src.length;) {
        var len = view.getUint32(at), type = String.fromCharCode(src[at + 4], src[at + 5], src[at + 6], src[at + 7]);
        if (type === 'pHYs') return blob;
        if (type === 'IDAT' || type === 'IEND') break;
        at += 12 + len;
      }
      var ppm = Math.round(dpi / 0.0254), chunk = new Uint8Array(21), cv = new DataView(chunk.buffer);
      cv.setUint32(0, 9);
      chunk.set([0x70, 0x48, 0x59, 0x73], 4);
      cv.setUint32(8, ppm); cv.setUint32(12, ppm); chunk[16] = 1;
      cv.setUint32(17, crc32(chunk.subarray(4, 17)));
      var cut = 33;   /* signature 8 + IHDR 25 */
      var out = new Uint8Array(src.length + 21);
      out.set(src.subarray(0, cut), 0); out.set(chunk, cut); out.set(src.subarray(cut), cut + 21);
      return new Blob([out], { type: 'image/png' });
    }, function () { return blob; });
  }
  function icon(path, size) {
    var s = size || 22;
    return '<svg viewBox="0 0 24 24" width="' + s + '" height="' + s + '" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' + path + '</svg>';
  }
  /* a risograph's red plate off register (KRPoster.FINISHES) in the
     editor: the plate (the frame lines and the words) on two canvases
     over the map, one carrying only its green and blue in place, one only
     its red shifted, each blended in by CSS (paintFinish's neighbour
     paintPlate): lighten on a sheet whose ink is lighter than its paper,
     darken on the other kind, so the channels a canvas does not carry
     pass through from below. plateInk: a theme color with the channels a
     canvas does not carry set to what its blend ignores (0 for lighten,
     255 for darken). */
  function plateInk(hex, keep, other) {
    var n = parseInt(String(hex).replace('#', ''), 16);
    var c = keep === 'r' ? [n >> 16 & 255, other, other] : [other, n >> 8 & 255, n & 255];
    return '#' + c.map(function (v) { return (v < 16 ? '0' : '') + v.toString(16); }).join('');
  }
  function lum(hex) {
    var n = parseInt(String(hex).replace('#', ''), 16);
    return 0.2126 * (n >> 16 & 255) + 0.7152 * (n >> 8 & 255) + 0.0722 * (n & 255);
  }
  /* wood grain: fractal noise stretched along a rail, tinted brown and
     multiplied over the moulding's lit gradient (kr-poster-maker.css) */
  function grainFilter(id, freq, seed) {
    return '<filter id="' + id + '" x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">' +
      '<feTurbulence type="fractalNoise" baseFrequency="' + freq + '" numOctaves="4" seed="' + seed + '"/>' +
      '<feColorMatrix type="matrix" values="0 0 0 0 0.25  0 0 0 0 0.14  0 0 0 0 0.05  0.9 0.9 0.9 0 -0.9"/></filter>';
  }
  function grainSvg(id) {
    return '<svg aria-hidden="true" focusable="false"><rect width="100%" height="100%" filter="url(#' + id + ')"/></svg>';
  }
  function isPhone() { return !!(root.matchMedia && root.matchMedia('(max-width: 899px)').matches); }
  function isTouch() { return !!(root.matchMedia && root.matchMedia('(hover: none) and (pointer: coarse)').matches); }
  /* the map libraries, named by their globals ('maplibregl', 'pmtiles'):
     resolves once all are on the page, fails if the page finished loading
     without them (a script that never arrived) */
  function needLibs(names) {
    var have = function () { return names.every(function (n) { return !!root[n]; }); };
    return new Promise(function (ok, no) {
      var check = function () {
        if (have()) { stop(); ok(); }
        else if (doc.readyState === 'complete') { stop(); no(new Error('the map libraries did not load')); }
      };
      /* a script's load event doesn't bubble, but the document sees it
         while capturing; the window's load ends the wait either way */
      var stop = function () { doc.removeEventListener('load', check, true); root.removeEventListener('load', check); };
      doc.addEventListener('load', check, true);
      root.addEventListener('load', check);
      check();
    });
  }

  function Maker(host, cfg) {
    var P = root.KRPoster;
    var self = this;
    var uid = ++uidN;
    var product = cfg.product || { variants: [], options: [] };
    var oz = 2;   /* set from the design below — see ozView */
    function fmtMoney(c) { return money(c, cfg.moneyFormat); }

    /* ── what a link opens on ───────────────────────────────────────── */
    /* A link may name the colors, the layout and the place in its query:
       the home page's color thumbs link ?color=blue, ?layout=headline names
       a layout by its key, and a place link is
       ?city=Paris&sub=France&lat=48.85&lon=2.35&zoom=12. Read only when
       the link carries no saved design (#d=), which always wins; the
       first save folds them into the hash and drops them from the query
       (saveNow), so a shared link says one thing. An unknown color and
       half a place (lat without lon) are ignored; a city named without a
       second line gets a blank one, not the section's. A city named
       WITHOUT a place (?city=Chicago, no lat and lon) is looked up once
       the editor stands (openPlace), the way a typed search is — the link
       an AI shopping agent or another page makes can name the city alone
       (2026-11-01; before, the words landed over the section's own map). */
    var OPENING_PARAMS = /^(color|layout|city|sub|lat|lon|zoom|state)=/;
    function linkOpening() {
      var qs = {}, o = {};
      root.location.search.replace(/^\?/, '').split('&').forEach(function (p) {
        var at = p.indexOf('=');
        if (at < 1) return;
        try { qs[decodeURIComponent(p.slice(0, at))] = decodeURIComponent(p.slice(at + 1).replace(/\+/g, ' ')).trim(); } catch (e) {}
      });
      /* a retired color's key (?color=cream) answers with the set that took
         its place, and the design carries that set's own key */
      var color = qs.color && P.theme(qs.color);
      if (color) o.theme = color.key;
      /* a set that asks for the shaded hills (Trailhead) opens with them on */
      if (color && color.relief) o.relief = true;
      /* ?layout=headline: a layout by its key (KRPoster.LAYOUTS) */
      if (qs.layout && P.LAYOUTS.some(function (L) { return L.key === qs.layout; })) o.layout = qs.layout;
      /* ?effect=risograph: a finish of its own (KRPoster.FINISHES) */
      if (qs.effect && P.finish(qs.effect)) o.finish = qs.effect;
      /* ?elevation=1: the hills shaded (Mapterhorn) */
      if (qs.elevation === '1') o.relief = true;
      var lat = parseFloat(qs.lat), lon = parseFloat(qs.lon), zoom = parseFloat(qs.zoom);
      if (isFinite(lat) && isFinite(lon) && Math.abs(lat) <= 85 && Math.abs(lon) <= 180) { o.lat = lat; o.lon = lon; }
      /* the map's own range (startMap): 3 is a continent, 16.5 a few streets */
      if (isFinite(zoom)) o.zoom = Math.min(16.5, Math.max(3, zoom));
      /* the Words fields' own caps (30 and 44) */
      if (qs.city) { o.title = qs.city.slice(0, 30); o.sub = (qs.sub || '').slice(0, 44); }
      /* ?state=CO opens on a whole state; its words and place are filled in
         from the states table once it loads (openState) */
      if (qs.state && /^[A-Za-z]{2}$/.test(qs.state)) o.state = qs.state.toUpperCase();
      /* the city to look up (openPlace) when the link gives no place: the
         second line narrows the search ("Paris, Texas") and stays as the
         link wrote it; the link's zoom, if any, stands over the place's own */
      if (qs.city && o.lat == null && !o.state) o.find = { city: qs.city, sub: qs.sub || '', zoom: o.zoom };
      return o;
    }

    /* ── the design ─────────────────────────────────────────────────── */
    var start = cfg.start || {};
    var d = {
      lat: num(start.lat, 40.7313), lon: num(start.lon, -74.0), zoom: num(start.zoom, 11.0), bearing: 0,
      theme: start.theme || 'bw', layout: start.layout || 'band', font: start.font || 'classic',
      orient: start.orient || 'portrait', size: cfg.size || '13x19',
      title: start.title || 'New York', sub: start.sub || 'United States', tag: '', tagAuto: true,
      buildings: start.buildings !== false, lines: 'fine', place: start.place || '',
      state: '', share: P.STATE_SHARE, ladder: P.STATE_LADDER
    };
    var fromHash = (root.location.hash.match(/[#&]d=([\w-]+)/) || [])[1];
    var saved = fromHash ? P.decode(fromHash) : null;
    if (saved) Object.keys(saved).forEach(function (k) { if (k !== 'v') d[k] = saved[k]; });
    /* no saved design: the link itself may name the colors and the place
       (the home page's color thumbs link ?color=blue). A city to look up
       is the link's errand, not part of the design (openPlace, at start) */
    var opening = saved ? null : linkOpening();
    var findPlace = opening ? opening.find : null;
    if (opening) { delete opening.find; Object.assign(d, opening); }
    /* buildings are always drawn (Kevin, 2026-10-24: "I want that on
       without a toggle") — an older saved design with them off gets them */
    d.buildings = true;
    /* one way to draw the streets (Kevin, 2026-10-25: "we can get rid of
       bold mode they dont make sense anyway") — a saved Bold design draws Fine */
    d.lines = 'fine';
    /* an unknown color falls back to Black & White; a retired one (Ink on
       Cream, Slated Earth — 2026-10-25) opens as the nearest look that
       stayed, and the design carries the live key from here on */
    d.theme = (P.theme(d.theme) || P.THEMES[0]).key;
    /* an unknown lettering letters in Classic; a local one (KRPoster.LOCAL,
       the place's own script) goes back to latinFont, the Latin face it was
       picked over, when the place changes to one that doesn't write in it */
    if (!P.font(d.font)) d.font = 'classic';
    if (d.local && !P.localFor(d.local.cc)) d.local = null;
    var latinFont = P.font(d.font).local ? 'classic' : d.font;
    /* HEADLINE LETTERS IN MODERN (Kevin, 2026-11-01: "modern should be the
       lettering that's turned on when the headline layout is selected"):
       picking Headline turns the lettering to Modern, and the face it
       replaced comes back when another layout is picked — unless the
       customer picked a face of their own in between, which then stays
       wherever they go. A local script (東京) is the customer's and stays.
       The Layout cards say so: Headline's letters in Modern while another
       layout is on, the others in the face that would come back. A link
       that opens on Headline (?layout=headline) opens in Modern too. */
    var fontBefore = null;   /* the face Headline replaced, until the customer picks one */
    function headlineFont() { var f = P.font(d.font); return f && f.local ? d.font : 'modern'; }
    /* the face layout `key` would letter in if picked now */
    function layoutFont(key) {
      if (key === 'headline') return d.layout === 'headline' ? d.font : headlineFont();
      return d.layout === 'headline' && fontBefore && d.font === 'modern' ? fontBefore : d.font;
    }
    /* the lettering's move when the layout changes to `next`; true when it moved */
    function layoutLetters(next) {
      var f = layoutFont(next);
      if (next === 'headline' && d.layout !== 'headline') fontBefore = f !== d.font ? d.font : null;
      else if (next !== 'headline') fontBefore = null;
      if (f === d.font) return false;
      d.font = f;
      if (!P.font(f).local) latinFont = f;
      return true;
    }
    if (opening && opening.layout === 'headline' && !P.font(d.font).local) {
      fontBefore = d.font === 'modern' ? null : d.font;
      d.font = 'modern'; latinFont = 'modern';
    }
    /* a whole state: the section says the street web's share of the road
       ink (Kevin's call, not the customer's); a saved design keeps the share
       and ladder it was made with, so its print matches what was seen. With
       the state option off, a state design opens as a city map at the
       state's centre. */
    var STATE_SHARE = Math.min(1, Math.max(0.2, num(cfg.stateShare, 65) / 100));
    if (!/^[A-Z]{2}$/.test(String(d.state || '')) || cfg.states === false) d.state = '';
    if (!d.state || !saved || !saved.state) { d.share = STATE_SHARE; d.ladder = P.STATE_LADDER; }
    else { d.share = num(d.share, STATE_SHARE); d.ladder = d.ladder || P.STATE_LADDER; }
    if (d.state) d.marker = null;
    /* the coordinates line names the marked spot when there is one, and
       the aimed spot otherwise (KRPoster.aimLat: on Classic and Lines the
       middle of the open map above the words) */
    function autoTag() { var mk = d.marker; return mk ? P.fmtCoords(mk.lat, mk.lon) : P.fmtCoords(P.aimLat(d), d.lon); }
    /* where Reset puts the map back: the place as it was opened or picked,
       as the spot to aim at (goTo) */
    function homeHere() { return { lat: P.aimLat(d), lon: d.lon, zoom: d.zoom }; }
    /* a new design opens AIMED at its place (the section's start city, a
       ?lat=&lon= link): the design keeps the camera, so it moves down by
       the aim. A saved design is its camera already. The paper size is
       read from the variant below, so the move waits for it; until then
       d.lat IS the aimed spot. */
    var openAim = !saved && !d.state ? d.lat : null;
    if (d.tagAuto) d.tag = openAim != null && !d.marker ? P.fmtCoords(openAim, d.lon) : autoTag();
    var home = openAim != null ? { lat: openAim, lon: d.lon, zoom: d.zoom } : homeHere();
    /* HOW DEEP THE MAP LOADS (2026-10-29; Kevin: "i'm just worried about
       slow 4g connections ... maybe it could detect slow tile loading or
       something notify the user"). On the live page a phone on slow 4G
       (1.6 Mbps) saw the map after 32-37 s: the veil waited for every
       street tile of the poster, ~135 tiles and 6-7 MB, and a desktop
       loaded the print's own depth (New York 861 tiles, 28 MB). Now the
       map comes in steps (depth.stage, below):
         first    a few coarse tiles, a zoom under the poster's (1-6 tiles,
                  0.2-0.8 MB), and the veil lifts on them
         tier     the streets at the whole poster's own depth (ozView)
                  loading over the coarse map, which sharpens tile by tile
                  as they land
         streets  every street in, the coarse map gone; the every-building
                  archive and the elevation landing
         done     all in
       Up close (the lock) the map loads the print's own depth (ozFull),
       and the print file always does (it never reads any of this). The
       coarse tiles are timed (Safari has no navigator.connection): on a
       slow connection the poster keeps a lighter map, one level under its
       own depth, without the every-building archive, and says so in one
       line under the poster. */
    var depth = { stage: 'first', fz: 0, slow: false, tierS: null, tierT: 0, check: 0, bounds: null };
    /* the print's own depth (KRPoster.ozFor): a whole-city frame draws
       from the z14 tiles, so an old town has every street */
    function ozFull() { return cfg.detail != null ? Math.pow(2, cfg.detail) : P.ozFor(d.zoom); }
    /* the whole poster's depth. Phones keep the depth they had before
       2026-10-25 (one level less, x4 at most, and the every-building
       archive only from a neighborhood frame in: Kevin, "Phones keep
       today's depth"); a desktop takes one level under the print's where
       an archive draws the houses (KRPoster.ozForView, measured against the
       print at screen size), the print's own elsewhere. A slow connection:
       one level lighter still. */
    function ozView() {
      if (cfg.detail != null) return Math.pow(2, cfg.detail);
      var v = isPhone() ? P.ozForPhone(d.zoom) : housesCovered() ? P.ozForView(d.zoom) : P.ozFor(d.zoom);
      return depth.slow ? Math.max(1, v / 2) : v;
    }
    /* whether an every-building archive holds the place: its bounds, once
       read (readBounds); until then the place is taken to be covered */
    function housesCovered() {
      if (!cfg.buildingsUrl) return false;
      if (!depth.bounds) return true;
      return depth.bounds.some(function (b) { return b && d.lon >= b[0] && d.lon <= b[2] && d.lat >= b[1] && d.lat <= b[3]; });
    }
    function readBounds() {
      if (depth.bounds || !cfg.buildingsUrl || !P.archiveBounds) return Promise.resolve();
      return Promise.all(String(cfg.buildingsUrl).split(',').map(function (u) { return u.trim(); }).filter(Boolean).map(P.archiveBounds))
        .then(function (list) { depth.bounds = list; });
    }
    oz = ozView();

    /* ── the variant ────────────────────────────────────────────────── */
    var opts = (product.options || []).map(function (o) { return typeof o === 'string' ? o : o.name; });
    var sizeIdx = opts.findIndex(function (n) { return /size/i.test(n); });
    var variant = null;
    var vParam = (root.location.search.match(/[?&]variant=(\d+)/) || [])[1];
    (product.variants || []).forEach(function (v) { if (String(v.id) === vParam) variant = v; });
    /* a saved design (#d=) without ?variant= — a link made before links
       carried the variant — opens on its OWN size, the cheapest finish of
       it: falling through to the global cheapest put every such link (the
       staff print link included) back on 13x19 and printed the wrong size */
    if (!variant && saved && saved.size && sizeIdx >= 0) {
      variant = cheapest((product.variants || []).filter(function (v) { return sizeOf(vo(v)[sizeIdx]) === saved.size; }));
    }
    /* no ?variant=: open on the cheapest one that can be bought (the 13x19
       print), so the price a customer first sees is the "from" price */
    if (!variant) {
      var buyable = (product.variants || []).filter(function (v) { return v.available; });
      variant = buyable.sort(function (a, b) { return a.price - b.price; })[0] || (product.variants || [])[0] || null;
    }
    var sel = variant ? vo(variant).slice(0, opts.length) : [];
    if (sizeIdx >= 0 && sel[sizeIdx]) d.size = sizeOf(sel[sizeIdx]) || d.size;
    /* the paper is known: a new design's camera moves down by the aim */
    if (openAim != null) d.lat = P.camLat(d, openAim);

    function num(v, dflt) { v = parseFloat(v); return isFinite(v) ? v : dflt; }
    function sizeOf(val) { var m = String(val).match(/(\d+(?:\.\d+)?)\s*[x×X]\s*(\d+(?:\.\d+)?)/); return m ? m[1] + 'x' + m[2] : null; }
    function vo(v) { return v.options || [v.option1, v.option2, v.option3]; }
    function findVariant(choice) {
      return (product.variants || []).filter(function (v) {
        var o = vo(v);
        for (var i = 0; i < choice.length; i++) if (o[i] !== choice[i]) return false;
        return true;
      })[0] || null;
    }
    /* the finish option: the one beside Size ("Style": Print, White Oak
       Frame, Canvas…), or the only option when there is no Size */
    var finishIdx = -1;
    if (opts.length === 2 && sizeIdx >= 0) finishIdx = 1 - sizeIdx;
    else if (opts.length === 1 && sizeIdx < 0 && !/^title$/i.test(opts[0])) finishIdx = 0;
    /* the Size step asks format → size → frame/finish when the options fit
       that shape; any other product gets one plain group per option */
    var structured = opts.length <= 2 && (sizeIdx >= 0 || finishIdx >= 0);
    function formatOf(v) {
      var t = String(finishIdx >= 0 ? (vo(v)[finishIdx] || '') : '').toLowerCase();
      if (/canvas/.test(t)) return 'canvas';
      if (/no.?frame|unframed|without frame/.test(t)) return 'poster';
      if (/frame|oak|walnut/.test(t)) return 'framed';
      return 'poster';
    }
    var formats = FORMATS.filter(function (f) {
      return (product.variants || []).some(function (v) { return formatOf(v) === f.key; });
    });

    /* ── DOM ────────────────────────────────────────────────────────── */
    host.innerHTML = '';
    host.classList.add('kpm');
    host.setAttribute('data-tab', 'place');
    var gh = 'kpm-gh-' + uid, gv = 'kpm-gv-' + uid;

    /* the bar: what is being made, the link, the price and the button */
    var bar = h('div', { class: 'kpm-bar' },
      '<div class="kpm-what"><b>' + esc(product.title || 'Custom map') + '</b><span class="kpm-state"></span></div>' +
      '<span class="kpm-saved">' + icon(I.check, 14) + 'Saved to this link</span>' +
      '<button type="button" class="kpm-share" aria-label="Share this design">' + icon(I.share, 16) + '<span>Share</span></button>' +
      '<span class="kpm-barbuy"><span class="kpm-price"></span>' +
      '<button type="button" class="kpm-btn kpm-btn-primary kpm-add"><span class="kpm-addlabel">Add to cart</span></button></span>');

    /* the stage: the poster (map + overlay canvas) and its finish */
    var stage = h('div', { class: 'kpm-stage' });
    var fit = h('div', { class: 'kpm-fit' });
    var poster = h('div', { class: 'kpm-poster' });
    var mapWrap = h('div', { class: 'kpm-mapwrap' });
    var mapEl = h('div', { class: 'kpm-map' });
    var over = h('canvas', { class: 'kpm-over', 'aria-hidden': 'true' });
    /* a risograph's plate off register: two more canvases over the words
       canvas, blended into the poster (paintPlate) */
    var plateCv = [h('canvas', { class: 'kpm-over kpm-plate', 'aria-hidden': 'true', hidden: true }),
                   h('canvas', { class: 'kpm-over kpm-plate', 'aria-hidden': 'true', hidden: true })];
    mapWrap.appendChild(mapEl);
    /* a whole state is drawn here, over the map element (which is hidden
       then — the planet tiles have nothing to show at a state's scale) */
    var stateCv = h('canvas', { class: 'kpm-statecv', 'aria-hidden': 'true', hidden: true });
    mapWrap.appendChild(stateCv);
    poster.appendChild(mapWrap);
    poster.appendChild(over);
    plateCv.forEach(function (c) { poster.appendChild(c); });
    fit.appendChild(poster);
    /* spans, not divs: the theme hides empty divs (see the CSS header) */
    fit.appendChild(h('span', { class: 'kpm-weave', 'aria-hidden': 'true' }));
    fit.appendChild(h('span', { class: 'kpm-lip', 'aria-hidden': 'true' }));
    fit.appendChild(h('div', { class: 'kpm-frame', 'aria-hidden': 'true' },
      '<i class="kpm-rt">' + grainSvg(gh) + '</i><i class="kpm-rb">' + grainSvg(gh) + '</i>' +
      '<i class="kpm-rl">' + grainSvg(gv) + '</i><i class="kpm-rr">' + grainSvg(gv) + '</i>'));
    stage.appendChild(fit);
    stage.appendChild(h('div', { class: 'kpm-defs', 'aria-hidden': 'true' },
      '<svg width="0" height="0" focusable="false"><defs>' + grainFilter(gh, '0.012 0.85', 7) + grainFilter(gv, '0.85 0.012', 11) + '</defs></svg>'));
    /* THE LOCK (Kevin, 2026-10-24: "add a lock option and when the lock
       option is on it zooms in on the actual canvas to get a better look at
       the details"). Locked, the map's framing holds still and zooming
       brings the POSTER closer, drawn sharp (see "a close look" below).
       Mapiful's phone editor has a lock in the same corner, but theirs only
       stops the map moving. lookEl takes the gestures while locked. */
    var lookEl = h('div', { class: 'kpm-look', 'aria-hidden': 'true' });
    stage.appendChild(lookEl);
    var lockBtn = h('button', { type: 'button', class: 'kpm-lock', 'aria-pressed': 'false', 'aria-label': 'Lock the map', title: 'Lock the map' }, icon(I.unlock, 18));
    stage.appendChild(lockBtn);
    var zoomBox = h('div', { class: 'kpm-zoom' },
      '<button type="button" data-z="in" aria-label="Zoom in">' + icon(I.plus, 18) + '</button>' +
      '<button type="button" data-z="out" aria-label="Zoom out">' + icon(I.minus, 18) + '</button>');
    stage.appendChild(zoomBox);
    var wholeBtn = h('button', { type: 'button', class: 'kpm-pill kpm-whole', hidden: true }, icon(I.whole, 16) + '<span>See the whole poster</span>');
    stage.appendChild(wholeBtn);
    /* (on a phone: Share joins Reset on the poster's corner, Mapiful-style —
       the title row with the bar's Share is gone there) */
    var tools = h('div', { class: 'kpm-tools' },
      '<button type="button" class="kpm-pill kpm-stageshare" aria-label="Share this design">' + icon(I.share, 16) + '</button>' +
      '<button type="button" class="kpm-pill kpm-reset" aria-label="Reset the map">' + icon(I.reset, 16) + '<span>Reset the map</span></button>' +
      '<span class="kpm-pill kpm-sizepill"></span>');
    stage.appendChild(tools);
    var dimW = h('div', { class: 'kpm-dim kpm-dim-w' }, '<span></span>'), dimH = h('div', { class: 'kpm-dim kpm-dim-h' }, '<span></span>');
    stage.appendChild(dimW); stage.appendChild(dimH);
    var cross = h('span', { class: 'kpm-cross', 'aria-hidden': 'true' });
    stage.appendChild(cross);
    var veil = h('div', { class: 'kpm-veil' }, '<span>Loading the map&hellip;</span>');
    stage.appendChild(veil);
    /* a slow connection's one line, on the wall under the poster (markSlow) */
    var slowNote = h('p', { class: 'kpm-slow', role: 'status', hidden: true },
      'Slow connection: showing a lighter map. Your poster still prints in full detail.');
    stage.appendChild(slowNote);
    var toastEl = h('p', { class: 'kpm-toast', role: 'status', 'aria-live': 'polite', hidden: true });
    stage.appendChild(toastEl);

    /* the step rail (a strip on a phone) */
    var rail = h('div', { class: 'kpm-rail', role: 'tablist', 'aria-label': 'Poster steps' });
    STEPS.forEach(function (s, i) {
      rail.appendChild(h('button', { type: 'button', role: 'tab', 'data-tab': s.id, id: 'kpm-tab-' + uid + '-' + s.id,
        'aria-controls': 'kpm-pane-' + uid + '-' + s.id, 'aria-selected': i === 0 ? 'true' : 'false' },
        icon(I[s.id], 22) + '<span class="kpm-tablabel">' + s.label + '</span>'));
    });
    /* the strip: the rail and, on a phone, the poster's thumb at its left.
       The page scrolls on a phone and the strip sticks to the top of the
       screen; once the poster has scrolled away the thumb shows it (the
       selected Layout card, which is this poster in its colors) and a tap
       goes back up to it. A desktop lays the rail out alone (display:
       contents) and never shows the thumb. */
    var strip = h('div', { class: 'kpm-strip' });
    var railThumb = h('button', { type: 'button', class: 'kpm-railthumb', 'aria-label': 'Back up to the poster', tabindex: '-1' },
      '<span class="kpm-railposter">' + icon(I.poster, 18) + '</span>');
    var railCv = h('canvas', { 'aria-hidden': 'true', hidden: true });
    railThumb.firstChild.appendChild(railCv);
    strip.appendChild(railThumb);
    strip.appendChild(rail);

    /* the panel: one pane per step, Back / Next under them */
    var panel = h('div', { class: 'kpm-panel' });
    var body = h('div', { class: 'kpm-body' });
    var foot = h('div', { class: 'kpm-foot' });
    /* phTitle / phLede: what a phone's step says instead (the Style pane
       heads the phone's Look step, which holds the layouts too) */
    function head(i, title, lede, phTitle, phLede) {
      var ph = function (alt, now) { return alt ? ' data-ph="' + esc(alt) + '" data-dk="' + esc(now) + '"' : ''; };
      return '<div class="kpm-head"><span class="kpm-kicker">Step ' + (i + 1) + ' of ' + STEPS.length + '</span>' +
        '<h2' + ph(phTitle, title) + '>' + title + '</h2>' + (lede ? '<p class="kpm-lede"' + ph(phLede, lede) + '>' + lede + '</p>' : '') + '</div>';
    }
    function pane(id, html) {
      return h('section', { class: 'kpm-pane', 'data-pane': id, role: 'tabpanel', id: 'kpm-pane-' + uid + '-' + id,
        'aria-labelledby': 'kpm-tab-' + uid + '-' + id, hidden: id !== 'place' }, html);
    }
    var LEDE_CITY = 'Search for a city, an address or a landmark, then drag the map to frame it.';
    var LEDE_STATE = 'Pick a state. Every road in it goes on the poster, cut to the state line.';
    var panePlace = pane('place',
      head(0, 'Place', LEDE_CITY) +
      /* the door: a city (the live map) or a whole state (its pre-rendered
         layer); the section can keep the state door closed */
      (cfg.states === false ? '' :
        '<div class="kpm-seg kpm-kind" role="group" aria-label="What goes on the poster">' +
        '<button type="button" data-kind="city" aria-pressed="true">' + icon(I.place, 16) + 'A city or place</button>' +
        '<button type="button" data-kind="state" aria-pressed="false">' + icon(I.state, 16) + 'A whole state</button></div>') +
      '<div class="kpm-citypick">' +
      '<div class="kpm-search"><label class="kpm-searchbox">' + icon(I.search, 20) +
      '<input type="search" placeholder="City, address or landmark" autocomplete="off" spellcheck="false" aria-label="Search for a place"></label>' +
      '<ul class="kpm-results" role="listbox" hidden></ul></div>' +
      '<div class="kpm-group"><span class="kpm-label">Popular places</span><div class="kpm-chips"></div></div></div>' +
      '<div class="kpm-statepick" hidden>' +
      '<label class="kpm-searchbox">' + icon(I.search, 20) +
      '<input type="search" placeholder="Find your state" autocomplete="off" spellcheck="false" aria-label="Find your state"></label>' +
      '<div class="kpm-states" role="listbox" aria-label="The fifty states"><span class="kpm-none">Loading the states&hellip;</span></div></div>' +
      '<div class="kpm-group"><span class="kpm-label">On the poster</span>' +
      '<div class="kpm-placecard"><span class="kpm-placeicon">' + icon(I.place, 20) + '</span>' +
      '<span class="kpm-placetext"><b class="kpm-placename"></b><span class="kpm-placemeta"></span></span></div></div>' +
      '<p class="kpm-tip kpm-tipcity">' + icon(I.hand, 20) + '<span>' +
      '<span class="kpm-desk">Drag the map on the poster to move it. Zoom with the + and &minus; buttons or your scroll wheel.</span>' +
      '<span class="kpm-touch">Drag the map on the poster to move it, and pinch to zoom.</span>' +
      ' The print matches the preview.</span></p>' +
      '<p class="kpm-tip kpm-tipstate" hidden>' + icon(I.state, 20) + '<span>Every road in the state, down to the smallest streets, ' +
      'each drawn at a weight that prints clean at this scale. ' +
      '<span class="kpm-desk">Zoom with the + and &minus; buttons or your scroll wheel to look closer.</span>' +
      '<span class="kpm-touch">Pinch to look closer.</span></span></p>');
    var paneStyle = pane('style',
      head(1, 'Style', 'Pick your poster’s colors.', 'Look', 'Pick your poster’s colors, then how the map and the words sit on it.') +
      '<div class="kpm-themes"></div>');
    var paneLayout = pane('layout',
      head(2, 'Layout', 'Pick how the map and the words sit on the poster.') +
      '<div class="kpm-cards kpm-layouts"></div>');
    var paneWords = pane('words',
      head(3, 'Words', 'Write up to three lines for the poster.') +
      '<div class="kpm-fields">' +
      '<label class="kpm-field"><span>Title</span><input type="text" data-w="title" maxlength="30" autocomplete="off" dir="auto"></label>' +
      '<label class="kpm-field"><span>Second line</span><input type="text" data-w="sub" maxlength="44" autocomplete="off" dir="auto"></label>' +
      '<label class="kpm-field"><span>Third line</span><input type="text" data-w="tag" maxlength="48" autocomplete="off"></label>' +
      /* a button, not a checkbox: the theme's base.css gives EVERY checkbox
         width:auto, which collapsed the switch on krmaps.com */
      '<button type="button" class="kpm-row kpm-rowplain kpm-switchrow" role="switch" aria-checked="true" data-w="tagAuto">' +
      '<span class="kpm-rowtext"><b>Use the map&rsquo;s coordinates</b></span><span class="kpm-switch" aria-hidden="true"></span></button>' +
      '</div>' +
      '<div class="kpm-group"><span class="kpm-label">Lettering</span><div class="kpm-fonts"></div></div>');
    var paneMarker = pane('marker',
      head(4, 'Marker', 'Mark one spot on the map, like a home or the place you met.') +
      '<div class="kpm-markers"></div>' +
      '<button type="button" class="kpm-btn kpm-btn-ghost kpm-wide" data-mark-here hidden>Move the marker to the center of the map</button>');
    var paneSize = pane('size',
      head(5, 'Size &amp; finish', structured && formats.length > 1 ? 'Pick the format first, then the size it comes in.' : 'Pick the size and finish.') +
      '<div class="kpm-optgroups"></div>' +
      '<div class="kpm-group"><span class="kpm-label">Orientation</span>' +
      '<div class="kpm-seg kpm-orient" role="group" aria-label="Orientation">' +
      '<button type="button" data-orient="portrait">' + icon(I.portrait, 16) + 'Portrait</button>' +
      '<button type="button" data-orient="landscape">' + icon(I.landscape, 16) + 'Landscape</button></div></div>' +
      '<div class="kpm-summary"><b>Your poster</b><span class="kpm-sumtext"></span></div>');
    [panePlace, paneStyle, paneLayout, paneWords, paneMarker, paneSize].forEach(function (p) { body.appendChild(p); });
    panel.appendChild(body);
    panel.appendChild(foot);

    /* the phone's buy bar (desktop buys from the bar) */
    var buy = h('div', { class: 'kpm-buy' },
      '<span class="kpm-buyprice"><span class="kpm-price"></span><span class="kpm-buywhat"></span></span>' +
      '<button type="button" class="kpm-btn kpm-btn-primary kpm-add"><span class="kpm-addlabel">Add to cart</span></button>');

    host.appendChild(bar);
    host.appendChild(strip);
    host.appendChild(panel);
    host.appendChild(stage);
    host.appendChild(buy);

    var q = function (s, el) { return (el || host).querySelector(s); };
    var qa = function (s, el) { return Array.prototype.slice.call((el || host).querySelectorAll(s)); };
    var stateEl = q('.kpm-state'), buyWhat = q('.kpm-buywhat'), sumText = q('.kpm-sumtext');
    var sizePill = q('.kpm-sizepill'), placeNameEl = q('.kpm-placename'), placeMetaEl = q('.kpm-placemeta');

    /* ── steps ──────────────────────────────────────────────────────── */
    /* phone or desktop, as of the last look (applyMode, on a resize past
       900px too) */
    var phoneMode = isPhone();
    function stepIndex(id) { for (var i = 0; i < STEPS.length; i++) if (STEPS[i].id === id) return i; return 0; }
    /* the steps that apply: a whole state has no map to point a marker at */
    function stepsNow() { return d.state ? STEPS.filter(function (st) { return st.id !== 'marker'; }) : STEPS; }
    function renumber() {
      var list = stepsNow();
      list.forEach(function (st, i) {
        var k = q('[data-pane="' + st.id + '"] .kpm-kicker', body);
        if (k) k.textContent = 'Step ' + (i + 1) + ' of ' + list.length;
      });
      q('[data-tab="marker"]', rail).hidden = !!d.state;
      /* a phone's Place step holds the marker: in and out with the state */
      if (phoneMode) syncPanes(host.getAttribute('data-tab'));
    }
    rail.addEventListener('click', function (e) {
      /* rail.contains: the host itself carries data-tab (the CSS keys off
         it), so a click on the rail's empty space would match the host */
      var b = e.target.closest('[data-tab]'); if (b && rail.contains(b)) showTab(b.getAttribute('data-tab'));
    });
    foot.addEventListener('click', function (e) {
      var b = e.target.closest('[data-go]'); if (b) showTab(b.getAttribute('data-go'));
    });
    /* the panes a step shows: its own on a desktop, its phone step's on a
       phone (the marker left out for a whole state) */
    function panesOf(id) {
      if (!phoneMode) return [id];
      return phoneStepOf(id).panes.filter(function (p) { return p !== 'marker' || !d.state; });
    }
    function syncPanes(id) {
      var show = panesOf(id);
      qa('.kpm-pane', body).forEach(function (p) { p.hidden = show.indexOf(p.getAttribute('data-pane')) < 0; });
    }
    function showTab(id, stay) {
      var i = stepIndex(id);
      id = STEPS[i].id;
      /* a phone opens the phone step that holds the pane asked for */
      if (phoneMode) { id = phoneStepOf(id).id; i = stepIndex(id); }
      host.setAttribute('data-tab', id);
      qa('[data-tab]', rail).forEach(function (b) {
        var j = stepIndex(b.getAttribute('data-tab'));
        b.setAttribute('aria-selected', j === i ? 'true' : 'false');
        b.classList.toggle('kpm-done', j < i);
      });
      syncPanes(id);
      renderFoot(i);
      renderPrice();
      /* the step opens at its top. A desktop scrolls the panel's body. A
         phone scrolls the PAGE: scrolled down past the poster (the strip
         stuck at the top), the page goes back to where the strip sticks,
         so the new step starts right under it; with the poster in view it
         stays put. */
      body.scrollTop = 0;
      panel.scrollTop = 0;
      if (phoneMode && !stay) {
        var y = root.scrollY || 0, top = stage.getBoundingClientRect().bottom + y;
        /* there the strip is stuck: say so now, not when the observer
           next reports (it waits for a paint) */
        if (y > top + 1) { root.scrollTo(0, top); setStuck(true); }
      }
      if (id === 'style' || id === 'layout') thumbs.request();
    }
    function renderFoot(i) {
      var list = stepsNow(), at = list.indexOf(STEPS[i]);
      /* a phone's Next walks its four steps; its strip is Back */
      if (phoneMode) {
        list = PHONE_STEPS.map(function (ps) { return { id: ps.id, label: ps.label }; });
        at = PHONE_STEPS.indexOf(phoneStepOf(STEPS[i].id));
      }
      var prev = list[at - 1], next = list[at + 1];
      foot.innerHTML =
        (prev ? '<button type="button" class="kpm-btn kpm-btn-ghost kpm-back" data-go="' + prev.id + '">' + icon(I.left, 16) + esc(prev.label) + '</button>' : '') +
        (next ? '<button type="button" class="kpm-btn kpm-btn-dark kpm-next" data-go="' + next.id + '">Next: ' + esc(next.label) + icon(I.right, 16) + '</button>'
          : '<button type="button" class="kpm-btn kpm-btn-primary kpm-add kpm-footadd"><span class="kpm-addlabel">Add to cart</span><span class="kpm-addprice"></span></button>');
    }

    /* ── geometry, scale, overlay ───────────────────────────────────── */
    var g = P.geom(d), scale = 1;
    function trimNum(n) { return String(+n.toFixed(2)); }
    function sizeLabel() { var i = P.sizeInches(d.size, d.orient); return trimNum(i.w) + ' × ' + trimNum(i.h) + ' in'; }
    /* THE POSTER IS LAID OUT AT ITS ON-SCREEN SIZE (fitStage): paper, map
       rect and words canvas in screen px, and only the map element keeps
       its design-unit size (MapLibre's camera and the tile depth depend on
       it), shrunk by scale/oz. Laying the whole poster out in design units
       (2400 x 3600 px for a 24x36) under one scale() drew a 24x36 on
       Kevin's Android phone with its bottom 13% blank (2026-10-24): 3600 px
       at 2.625 device px each is 9,450, and 8,192/9,450 = 87% — the phone
       drew that layer at full size before shrinking it. */
    function applyGeom() {
      var g0 = g;
      g = P.geom(d);
      /* the poster changed shape (size, orientation, layout): a close look
         goes back to the whole poster */
      if (look.hold && !sameGeom(g0, g)) lookReset();
      var t = P.theme(d.theme);
      poster.style.background = t.paper;
      /* a whole state sits on the paper, cut to its outline */
      mapWrap.style.background = d.state ? t.paper : t.land;
      mapWrap.classList.toggle('kpm-round', g.shape === 'circle');
      if (!look.win) {
        mapEl.style.width = (g.map.w * oz) + 'px';
        mapEl.style.height = (g.map.h * oz) + 'px';
      }
      var inch = P.sizeInches(d.size, d.orient);
      dimW.firstChild.textContent = trimNum(inch.w) + ' in';
      dimH.firstChild.textContent = trimNum(inch.h) + ' in';
      fitStage();
      if (map && !look.win && !d.state) map.resize();
      placeSoon();
    }
    /* after a new layout, size or orientation, the same spot stays aimed
       (its aim moves with the layout: Classic's sits higher than Band's) */
    function reaim(aim) {
      if (d.state || look.hold) return;
      d.lat = P.camLat(d, aim);
      if (map) map.jumpTo({ center: [d.lon, d.lat] });
    }
    function sameGeom(a, b) {
      return a.W === b.W && a.H === b.H && !!a.map === !!b.map &&
        (!a.map || (a.map.x === b.map.x && a.map.y === b.map.y && a.map.w === b.map.w && a.map.h === b.map.h));
    }
    /* a close look (the lock): look.m = how many times closer than the
       whole poster; look.cx, look.cy = the poster point (design units) at
       the middle of the stage's room. hold = a look is on (the map element
       is not at the design's camera, so its moves aren't the customer's);
       quiet = a moment after one ends, while MapLibre's own resize lands */
    var look = { locked: false, m: 1, cx: 0, cy: 0, win: null, hold: false, quiet: false, bldAll: false, hinted: false };
    var fitScale = 1, fitX = 0, fitY = 0, roomX = 0, roomY = 0, stageW = 0, stageH = 0;
    function fitStage() {
      var r = stage.getBoundingClientRect();
      if (!r.width || !r.height) return;
      stageW = r.width; stageH = r.height;
      /* room around the poster: the rulers on a desktop; on a phone the
         poster takes the whole stage, its controls sitting on its corners
         (Kevin, with Mapiful's phone editor: "look at how much more space
         mapiful has") */
      var phone = isPhone();
      var pl = phone ? 16 : 64, pr = phone ? 16 : 64, pt = phone ? 16 : 56, pb = phone ? 16 : 48;
      /* the slow-connection line takes a strip of wall under the poster
         (hidden up close, where it has no height) */
      var sh = slowNote && !slowNote.hidden ? slowNote.offsetHeight : 0;
      if (sh) pb += sh + (phone ? 10 : 12);
      /* a frame's moulding is 0.65 in and covers 0.25 in of the print's
         edge (Kevin's desk-size frames, measured off FrameOptions.jpg) */
      var framed = host.hasAttribute('data-frame'), fw = framed ? 65 : 0, rb = framed ? 25 : 0;   /* design units */
      var ext = 2 * (fw - rb);
      fitScale = Math.max(0.05, Math.min((r.width - pl - pr) / (g.W + ext), (r.height - pt - pb) / (g.H + ext)));
      look.m = Math.min(look.m, lookMax());
      scale = fitScale * look.m;
      host.style.setProperty('--kpm-frame', (fw * scale).toFixed(1) + 'px');
      host.style.setProperty('--kpm-rabbet', (rb * scale).toFixed(1) + 'px');
      var pw = g.W * scale, ph = g.H * scale;
      roomX = pl + (r.width - pl - pr) / 2;
      roomY = pt + (r.height - pt - pb) / 2;
      if (look.m <= 1) { look.cx = g.W / 2; look.cy = g.H / 2; }
      else {
        /* up close the poster moves until its edge (the frame's, with a
           little wall) is just inside the stage; a side that fits stays in
           the middle */
        var e = (fw - rb) * scale + 24;
        look.cx = pw + 2 * e <= r.width ? g.W / 2 : Math.max((roomX - e) / scale, Math.min((roomX + pw + e - r.width) / scale, look.cx));
        look.cy = ph + 2 * e <= r.height ? g.H / 2 : Math.max((roomY - e) / scale, Math.min((roomY + ph + e - r.height) / scale, look.cy));
      }
      var fx = roomX - look.cx * scale, fy = roomY - look.cy * scale;
      fitX = fx; fitY = fy;
      fit.style.width = pw + 'px';
      fit.style.height = ph + 'px';
      poster.style.width = pw + 'px';
      poster.style.height = ph + 'px';
      mapWrap.style.left = (g.map.x * scale) + 'px';
      mapWrap.style.top = (g.map.y * scale) + 'px';
      mapWrap.style.width = (g.map.w * scale) + 'px';
      mapWrap.style.height = (g.map.h * scale) + 'px';
      /* the map element: the whole map, or in a close look the part of it
         drawn last (lookRender) */
      var w = look.win;
      mapEl.style.left = (w ? w.u0 * scale : 0) + 'px';
      mapEl.style.top = (w ? w.v0 * scale : 0) + 'px';
      mapEl.style.transform = 'scale(' + (scale / oz) + ')';
      fit.style.left = fx + 'px';
      fit.style.top = fy + 'px';
      /* the Marker step's crosshair sits on the map's middle */
      cross.style.left = (fx + (g.map.x + g.map.w / 2) * scale) + 'px';
      cross.style.top = (fy + (g.map.y + g.map.h / 2) * scale) + 'px';
      var off = (fw - rb) * scale + 14;
      dimW.style.left = fx + 'px'; dimW.style.width = pw + 'px'; dimW.style.top = (fy + ph + off) + 'px';
      dimH.style.top = fy + 'px'; dimH.style.height = ph + 'px'; dimH.style.left = (fx + pw + off) + 'px';
      if (map && !look.hold && !d.state) {
        var prr = previewRatio();
        if (Math.abs(map.getPixelRatio() - prr) > 0.005) map.setPixelRatio(prr);
      }
      if (d.state) drawState();
      paintOver();
      lookButtons();
      if (look.m > 1) lookSoon();
    }
    var overQueued = false;
    function drawOver() {
      if (overQueued) return;
      overQueued = true;
      (root.requestAnimationFrame || setTimeout)(function () { overQueued = false; paintOver(); });
    }
    /* the words canvas covers the poster, or in a close look only the part
       of the poster on the stage: a canvas as big as a 24x36 seen up close
       would be far past what a phone can draw */
    function paintOver() {
      var dpr = root.devicePixelRatio || 1;
      var pw = g.W * scale, ph = g.H * scale, x0 = 0, y0 = 0, x1 = pw, y1 = ph;
      if (look.m > 1) {
        x0 = Math.max(0, Math.floor(-fitX)); y0 = Math.max(0, Math.floor(-fitY));
        x1 = Math.max(x0 + 1, Math.min(pw, Math.ceil(stageW - fitX)));
        y1 = Math.max(y0 + 1, Math.min(ph, Math.ceil(stageH - fitY)));
      }
      var cw = Math.max(1, Math.round((x1 - x0) * dpr)), ch = Math.max(1, Math.round((y1 - y0) * dpr));
      if (over.width !== cw) over.width = cw;
      if (over.height !== ch) over.height = ch;
      over.style.left = x0 + 'px';
      over.style.top = y0 + 'px';
      over.style.width = (x1 - x0) + 'px';
      over.style.height = (y1 - y0) + 'px';
      var ctx = over.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, cw, ch);
      ctx.translate(-x0 * dpr, -y0 * dpr);
      var m = P.finishOf(d).misreg;
      P.drawOverlay(ctx, d, scale * dpr, g, m ? { noPlate: true } : null);
      paintPlate(m, x0, y0, x1, y1, cw, ch, dpr);
      paintFinish(x0, y0, x1, y1, cw, ch, dpr);
    }
    /* THE PLATE OFF REGISTER (a risograph, KRPoster.FINISHES): the frame
       lines and the words drawn on two canvases where the words canvas is,
       one with the theme's green and blue in place, one with its red
       m.dx/m.dy design units off, blended into the poster (plateInk); the
       map, the fade, the marker and the credit below them stay on
       register. The print composites the same two drawings per pixel
       (KRPoster.drawPlateOff). */
    function paintPlate(m, x0, y0, x1, y1, cw, ch, dpr) {
      plateCv.forEach(function (c) { c.hidden = !m; });
      if (!m) return;
      var t = P.theme(d.theme) || P.THEMES[0];
      var mode = lum(t.ink) >= lum(t.paper) ? 'lighten' : 'darken', other = mode === 'lighten' ? 0 : 255;
      plateCv.forEach(function (c, i) {
        var keep = i ? 'r' : 'gb';
        var tt = Object.assign({}, t, { ink: plateInk(t.ink, keep, other), frame: plateInk(t.frame, keep, other) });
        if (t.tag) tt.tag = plateInk(t.tag, keep, other);
        if (c.width !== cw) c.width = cw;
        if (c.height !== ch) c.height = ch;
        c.style.left = x0 + 'px';
        c.style.top = y0 + 'px';
        c.style.width = (x1 - x0) + 'px';
        c.style.height = (y1 - y0) + 'px';
        c.style.mixBlendMode = mode;
        var cx = c.getContext('2d');
        cx.setTransform(1, 0, 0, 1, 0, 0);
        cx.clearRect(0, 0, cw, ch);
        cx.translate(-x0 * dpr + (i ? m.dx * scale * dpr : 0), -y0 * dpr + (i ? m.dy * scale * dpr : 0));
        P.drawPlate(cx, d, scale * dpr, g, { theme: tt });
      });
    }
    /* THE FINISH (KRPoster.FINISHES: film grain, risograph): each layer its
       own canvas over the words and the plate, covering what the words
       canvas covers, blended into the poster by CSS (mix-blend-mode; the
       poster isolates them, kr-poster-maker.css); a risograph's plate off
       register is paintPlate above */
    var finCv = [];
    function paintFinish(x0, y0, x1, y1, cw, ch, dpr) {
      var f = P.finishOf(d), layers = f.layers;
      while (finCv.length < layers.length) {
        var c = h('canvas', { class: 'kpm-fin', 'aria-hidden': 'true' });
        poster.appendChild(c);
        finCv.push(c);
      }
      finCv.forEach(function (c, i) {
        var L = layers[i];
        c.hidden = !L;
        if (!L) return;
        if (c.width !== cw) c.width = cw;
        if (c.height !== ch) c.height = ch;
        c.style.left = x0 + 'px';
        c.style.top = y0 + 'px';
        c.style.width = (x1 - x0) + 'px';
        c.style.height = (y1 - y0) + 'px';
        c.style.mixBlendMode = L.blend;
        var fx = c.getContext('2d');
        fx.setTransform(1, 0, 0, 1, 0, 0);
        fx.clearRect(0, 0, cw, ch);
        fx.translate(-x0 * dpr, -y0 * dpr);
        P.drawFinishLayer(fx, L, scale * dpr, g, 'source-over');
      });
    }

    /* ── the map ────────────────────────────────────────────────────── */
    var map = null;
    /* the map element is oz× its design size and shown at scale/oz, so one
       screen pixel per canvas pixel is scale·dpr/oz */
    /* SUPERSAMPLED PREVIEW: at poster size a street is a fraction of a
       screen pixel, and MapLibre draws any sub-pixel line at half strength
       or more (its antialiasing floor), so a 1:1 preview showed far more
       ink than the print has (Kevin's "muddy"). The canvas is drawn at
       ~1.4 px per design unit (never under 2x the screen, never over 4x)
       and shrunk by the compositor: Bold Chicago with every building
       measured 179.6 tone at 2x against the print's 196.6, and 194.2 at
       1.4 px/unit. A phone's dense screen gets there with less
       supersampling. ?kpmSS= forces a factor for testing; ?kpmPdb=1 keeps
       the canvas readable for the harness. */
    var SS = +((root.location.search.match(/[?&]kpmSS=([\d.]+)/) || [])[1]) || 0;
    var PDB = /[?&]kpmPdb=1/.test(root.location.search);
    /* ?kpmRiso=0.5: the risograph's red plate that many design units off
       (half that down), for trying strengths in the preview; a design never
       carries it */
    var RISO = +((root.location.search.match(/[?&]kpmRiso=([\d.]+)/) || [])[1]) || 0;
    if (RISO && P.finish('risograph')) P.finish('risograph').misreg = { dx: RISO, dy: RISO / 2 };
    function previewRatio() {
      var base = scale * (root.devicePixelRatio || 1);   /* canvas px per design unit at 1:1 */
      var ss = SS || Math.max(2, Math.min(4, 1.4 / base));
      return Math.min(4, Math.max(0.08, ss * base / oz));
    }
    /* PHONES draw every building only from a neighborhood-size frame in
       (design zoom 13, ~5 miles across on a 13x19). Measured in dense
       Chicago on a phone-size screen (Kevin, 2026-10-24: "Is that insane
       for a website to do to a mobile device tho" → "load them only when
       closer"): ~113,000 footprints there, 3.8 MB, ~80 MB of page memory,
       against ~1 M, 10 MB and ~400 MB at a city frame, where a house is
       under half a screen pixel anyway. Desktop and the print file keep
       every building at every frame. */
    var PHONE_BUILDINGS_FROM = 13;
    /* the lighter map a slow connection keeps (never up close): no
       every-building archive, and the hills and the elevation lines read
       two zoom levels coarser (a sixteenth of the elevation tiles) */
    function light() { return depth.slow && !look.hold; }
    function styleFor() {
      /* until every street is in, the coarse map lies under the poster's
         own, and the buildings archive and the elevation wait (their tiles
         are the heaviest to read: with them asked for at once, a desktop's
         streets came 4 s later) */
      var coarse = depth.stage === 'first' || depth.stage === 'tier', early = coarse && !look.hold, lite = light();
      var st = P.style(d, { pmtilesUrl: cfg.pmtilesUrl, buildingsUrl: lite || early ? '' : cfg.buildingsUrl, terrainUrl: cfg.terrainUrl, oz: oz,
        buildingsFrom: isPhone() && !look.bldAll ? PHONE_BUILDINGS_FROM : null,
        elevation: !early, reliefOpts: lite ? { depth: -2 } : null, demDrop: lite ? 2 : 0 });
      return coarse ? withFirst(st, depth.stage === 'first') : st;
    }
    /* THE COARSE FIRST MAP: every layer that reads the planet ('pm') again
       on a second source of the same archive capped at depth.fz, so MapLibre
       reads those few tiles and stretches them over the poster (each parsed
       again at the camera's zoom, so every line keeps its width). Alone at
       first; then under the poster's own layers while their tiles land, each
       covering its part of the coarse map (the land and the water fill a
       tile). */
    function withFirst(st, alone) {
      var coarse = st.layers.filter(function (l) { return l.source === 'pm'; }).map(function (l) {
        return Object.assign({}, l, { id: 'first-' + l.id, source: 'pm0' });
      });
      var src = { pm0: { type: 'vector', url: st.sources.pm.url, maxzoom: depth.fz } };
      if (alone) return { version: 8, sources: src, layers: [st.layers[0]].concat(coarse) };
      return { version: 8, sources: Object.assign(src, st.sources), layers: [st.layers[0]].concat(coarse, st.layers.slice(1)) };
    }
    /* the coarse map's tile zoom: a level under the poster's, for a 13x19's
       map (bigger paper covering the same ground reads the same tiles), and
       always under the poster's own tiles */
    function firstZoom() {
      var fz = Math.floor(d.zoom - 1 + Math.log(1280 / Math.sqrt(g.map.w * g.map.h)) / Math.LN2);
      return Math.max(0, Math.min(Math.floor(P.camZoom(d, oz)) - 1, fz));
    }
    function setDepth(stage) {
      depth.stage = stage;
      host.setAttribute('data-kpm-depth', stage);
    }
    /* a set with contour lines or summits (Trailhead) draws them from a
       krdem:// source named for the design's window (KRPoster.demUrl,
       rounded so small pans keep it); once a pan or zoom leaves that
       window, point the sources at the new one */
    function syncTerrain() {
      if (!map) return;
      var t = P.theme(d.theme);
      if (!t || !(t.contours || t.summits) || d.state || !P.demUrl) return;
      ['contours', 'summits'].forEach(function (k) {
        if (!t[k]) return;
        var src = map.getSource(k); if (!src || !src.setData) return;
        var url = P.demUrl(k, d, cfg.terrainUrl, light() ? 2 : 0);
        if ((src._krUrl || src._data) !== url) { src._krUrl = url; src.setData(url); }
      });
    }
    /* the frame crossed into the other depth band (or the connection
       turned out slow): re-lay the map element at the new size and put the
       camera back on the same ground. True when it moved. */
    var switching = false;
    function switchOz() {
      var want = ozView();
      if (want === oz || !map) return false;
      /* resize/min-zoom changes fire 'move' synchronously; hold the design
         still so onMove can't read the old camera through the new oz */
      var keep = { lat: d.lat, lon: d.lon, zoom: d.zoom };
      switching = true;
      oz = want;
      mapEl.style.width = (g.map.w * oz) + 'px';
      mapEl.style.height = (g.map.h * oz) + 'px';
      mapEl.style.transform = 'scale(' + (scale / oz) + ')';
      map.setMinZoom(P.camZoom({ zoom: 3 }, oz));
      map.setMaxZoom(P.camZoom({ zoom: 16.5 }, oz));
      map.resize();
      map.setPixelRatio(previewRatio());
      map.jumpTo({ center: [keep.lon, keep.lat], zoom: P.camZoom(keep, oz) });
      switching = false;
      d.lat = keep.lat; d.lon = keep.lon; d.zoom = keep.zoom;
      map.setStyle(styleFor());
      return true;
    }
    /* the map starts once MapLibre and the PMTiles reader are on the page;
       until then the veil says it is loading, and whatever the customer
       does meanwhile (a place, colors, the lock) is in the design it opens
       on */
    var mapWait = null;
    function startMap() {
      if (map || mapWait) return;
      mapWait = needLibs(['maplibregl', 'pmtiles']).then(function () {
        mapWait = null;
        /* (a whole state picked meanwhile has no map until the way back);
           the style cards wait for the coarse map (firstDrawn) */
        if (!map && !d.state) makeMap();
        else thumbs.request();
      }, function () {
        mapWait = null;
        veil.innerHTML = '<span>The map could not load. Please refresh the page.</span>';
      });
    }
    /* THE COARSE MAP'S TILES, READ EARLY: the PMTiles reader is on the page
       seconds before MapLibre (~270 KB; on a slow phone it landed at 9.6 s,
       and only then were the first tiles asked for, the map showing at
       16 s). So the coarse map's few tiles are read as soon as the reader is
       here, and wait in KRPoster's kept reads for MapLibre's own ask; the
       slow-connection clock starts with them (early: fetchStats then) */
    var early = null;
    function readFirstEarly() {
      needLibs(['pmtiles']).then(function () {
        if (map || d.state || early || !P.prefetch) return;
        early = P.fetchStats();
        if (!isPhone()) readBounds();
        var fz = firstZoom(), n = Math.pow(2, fz), ws = 512 * Math.pow(2, d.zoom);
        var sy = Math.sin(d.lat * Math.PI / 180);
        var cx = (d.lon + 180) / 360, cy = 0.5 - Math.log((1 + sy) / (1 - sy)) / (4 * Math.PI);
        var hw = g.map.w / 2 / ws, hh = g.map.h / 2 / ws;
        if (d.bearing) hw = hh = Math.sqrt(hw * hw + hh * hh);
        P.prefetch(cfg.pmtilesUrl || P.DEFAULT_PMTILES, fz,
          Math.floor((cx - hw) * n), Math.floor((cy - hh) * n), Math.floor((cx + hw) * n), Math.floor((cy + hh) * n));
      }, function () {});
    }
    /* slower than this, the poster keeps the lighter map (kbit/s; slow 4G
       is 1,600, a 4G phone's usual 9,000). The coarse tiles are read while
       the page's own scripts, fonts and analytics still load beside them,
       so they come slower than the line can carry: on the development
       theme 480-600 on slow 4G and 2,000-3,400 on 4G (SLOW_FIRST_KBPS).
       The streets come later, beside less: 1,400-1,600 and ~8,000. */
    var SLOW_FIRST_KBPS = 1200, SLOW_KBPS = 2000;
    /* how fast tiles came between two fetchStats readings, in kbit/s: the
       bytes over the time at least one was on the wire; null under 150 KB,
       too little to tell a slow connection from a far server */
    function tileRate(a, b) {
      var bytes = b.bytes - a.bytes, ms = b.busyMs - a.busyMs;
      return bytes < 153600 || !(ms > 0) ? null : bytes * 8 / ms;
    }
    /* the coarse map is up: the veil lifts, the style cards may draw, and
       how fast its tiles came says what loads next */
    function firstDrawn(s0) {
      if (!d.state) veil.hidden = true;
      thumbs.go();
      var r = tileRate(s0, P.fetchStats());
      if (r != null && r < SLOW_FIRST_KBPS) markSlow();
      setDepth('tier');
      depth.tierS = P.fetchStats();
      depth.tierT = Date.now();
      if (!switchOz()) map.setStyle(styleFor());
      watchTier();
    }
    /* the poster's own tiles landing: once every street is in, the coarse
       map goes and the buildings and the elevation are asked for; once
       everything is, the stage is done (never mid-look: up close only the
       part on the stage loads) */
    function tierProgress() {
      if (!map || look.hold || (depth.stage !== 'tier' && depth.stage !== 'streets')) return;
      var tm = map.style && map.style.tileManagers;
      if (depth.stage === 'tier' && tm && tm.pm && map.isSourceLoaded('pm')) {
        setDepth('streets');
        map.setStyle(styleFor());
      }
      if (depth.stage === 'streets' && map.loaded()) setDepth('done');
    }
    /* while the streets land the connection is timed again (the coarse
       tiles can come from the browser's cache, and a connection can slow
       down); not while the buildings land, whose reading keeps a computer
       busy enough to slow its own downloads */
    function watchTier() {
      clearInterval(depth.check);
      depth.check = setInterval(function () {
        if (depth.slow || depth.stage !== 'tier' || !map) { clearInterval(depth.check); return; }
        if (look.hold || Date.now() - depth.tierT < 4000) return;
        var r = tileRate(depth.tierS, P.fetchStats());
        if (r != null && r < SLOW_KBPS) { markSlow(); if (!switchOz()) map.setStyle(styleFor()); }
      }, 1000);
    }
    /* a slow connection: the lighter map (ozView, light) and its one line
       under the poster (Kevin's words: plain, no time, never over the map) */
    function markSlow() {
      if (depth.slow) return;
      depth.slow = true;
      host.setAttribute('data-kpm-slow', '');
      slowNote.hidden = false;
      fitStage();
    }
    function makeMap() {
      if (!P.ensureProtocol()) { veil.innerHTML = '<span>The map could not load. Please refresh the page.</span>'; return; }
      /* a place picked while the libraries loaded can need the other depth */
      var want = ozView();
      if (want !== oz) { oz = want; applyGeom(); }
      /* a desktop: whether an every-building archive draws the houses here
         (ozView), read beside the coarse tiles; a late answer moves the
         poster to its depth once the coarse map is up */
      if (!isPhone()) readBounds().then(function () { if (map && !look.hold && !d.state && depth.stage !== 'first') switchOz(); });
      depth.fz = firstZoom();
      setDepth('first');
      var s0 = early || P.fetchStats();
      map = new root.maplibregl.Map({
        container: mapEl, style: styleFor(), center: [d.lon, d.lat], zoom: P.camZoom(d, oz), bearing: d.bearing || 0,
        pixelRatio: previewRatio(),
        attributionControl: false, dragRotate: false, pitchWithRotate: false, touchPitch: false, maxPitch: 0,
        minZoom: P.camZoom({ zoom: 3 }, oz), maxZoom: P.camZoom({ zoom: 16.5 }, oz), fadeDuration: 0,
        renderWorldCopies: false,
        canvasContextAttributes: { preserveDrawingBuffer: PDB, antialias: true }
      });
      map.touchZoomRotate.disableRotation();
      map.keyboard.disableRotation && map.keyboard.disableRotation();
      /* the veil lifts on the coarse map (only its few tiles are asked for
         until then); the streets and the buildings fill in under a visible
         map (tierProgress) */
      map.once('idle', function () { firstDrawn(s0); });
      map.on('sourcedata', tierProgress);
      map.on('idle', tierProgress);
      map.on('move', onMove);
      map.on('moveend', function () { if (look.hold || look.quiet || d.state) return; switchOz(); syncTerrain(); saveSoon(); thumbs.request(); });
      map.on('error', function (e) { if (root.console) console.warn('[poster-maker]', e && e.error && e.error.message); });
      /* locked, or looking up close, before the map was there */
      if (look.locked) mapGestures();
      if (look.hold && look.m > 1) lookSoon();
    }
    function onMove() {
      if (switching || look.hold || look.quiet || d.state) return;
      var c = map.getCenter();
      d.lat = c.lat; d.lon = c.lng;
      d.zoom = map.getZoom() - Math.log(oz) / Math.LN2;
      if (d.tagAuto && !d.marker) { d.tag = autoTag(); syncWords(true); }
      if (d.tagAuto || d.marker) drawOver();
      placeSoon();
    }
    zoomBox.addEventListener('click', function (e) {
      var b = e.target.closest('[data-z]'); if (!b) return;
      var zin = b.getAttribute('data-z') === 'in';
      /* locked: the poster comes closer, the map holds still */
      if (look.locked) { lookZoomTo(zin ? look.m * 2 : look.m / 2); return; }
      if (!map) return;
      if (zin) map.zoomIn({ duration: 250 }); else map.zoomOut({ duration: 250 });
    });
    q('.kpm-reset', tools).addEventListener('click', function () { goTo(home.lat, home.lon, home.zoom); });
    /* lat, lon = the spot to AIM at (a place, a city's box middle, home):
       the camera goes where that spot lands in the open map (KRPoster.camLat) */
    function goTo(lat, lon, zoom, bbox) {
      /* a new place (or Reset): the whole poster again first */
      if (look.hold) lookReset();
      if (bbox && bbox.length === 4) {
        var cam = map ? map.cameraForBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]], { padding: 40 * oz }) : boxCamera(bbox);
        var dz = cam ? cam.zoom - Math.log(oz) / Math.LN2 : zoom;
        zoom = Math.max(9, Math.min(15.5, dz));
        if (cam) { lat = cam.center.lat; lon = cam.center.lng; }
      }
      lat = P.camLat(Object.assign({}, d, { zoom: zoom }), lat);
      if (!map) { d.lat = lat; d.lon = lon; d.zoom = zoom; return; }
      map.jumpTo({ center: [lon, lat], zoom: P.camZoom({ zoom: zoom }, oz) });
    }
    /* MapLibre's cameraForBounds before the map is there (a place picked
       while the libraries load): the box [w, s, e, n] fitted into the map
       element with the same padding, in web mercator (512-px tiles, the
       poster never turns), the element measured in whole pixels as MapLibre
       reads its container */
    function boxCamera(b) {
      var x = function (lon) { return (180 + lon) / 360; };
      var y = function (lat) {
        var s = Math.sin(Math.max(-85.051129, Math.min(85.051129, lat)) * Math.PI / 180);
        return 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI);
      };
      var x0 = x(b[0]), x1 = x(b[2]), y0 = y(b[3]), y1 = y(b[1]), pad = 40 * oz;
      var W = mapEl.clientWidth || Math.round(g.map.w * oz), H = mapEl.clientHeight || Math.round(g.map.h * oz);
      var k = Math.min((W - 2 * pad) / ((x1 - x0) * 512), (H - 2 * pad) / ((y1 - y0) * 512));
      if (!(k > 0) || !isFinite(k)) return null;
      return { zoom: Math.log(k) / Math.LN2,
        center: { lng: (x0 + x1) / 2 * 360 - 180, lat: Math.atan(Math.sinh(Math.PI * (1 - (y0 + y1)))) * 180 / Math.PI } };
    }

    /* ── a close look (the lock) ────────────────────────────────────── */
    /* Locked, zooming brings the POSTER closer, as far as the print file's
       own pixels (600 DPI = 6 per design unit) at one screen pixel each.
       The map is drawn again, sharp, for just the part on the stage plus a
       margin (lookRender), at the print's own tile depth once close enough
       (ozLook; the whole poster on a screen loads its own, ozView), so up
       close shows what prints. A picture of the whole map taken
       as the look begins (the ghost) lies under it, so a part not drawn yet
       shows soft instead of blank. The words canvas covers only the stage
       too (paintOver). */
    var LOOK_MARGIN = 0.2, LOOK_MAX_DIM = 4096, LOOK_MAX_AREA = 12e6;
    var ghost = h('canvas', { class: 'kpm-ghost', 'aria-hidden': 'true', hidden: true });
    mapWrap.insertBefore(ghost, mapEl);
    function lookMax() {
      return Math.max(1, Math.max(1.5, 6 / (root.devicePixelRatio || 1)) / fitScale);
    }
    function clampM(m) { return Math.max(1, Math.min(lookMax(), m)); }
    /* the place under a point of the map (design units from its top left),
       for the design's own camera */
    function geoAt(u, v) {
      var MC = root.maplibregl.MercatorCoordinate;
      var c = MC.fromLngLat([d.lon, d.lat]);
      var ws = 512 * Math.pow(2, P.camZoom(d, oz));
      var dx = (u - g.map.w / 2) * oz, dy = (v - g.map.h / 2) * oz;
      var b = (d.bearing || 0) * Math.PI / 180, cs = Math.cos(b), sn = Math.sin(b);
      return new MC(c.x + (dx * cs - dy * sn) / ws, c.y + (dx * sn + dy * cs) / ws).toLngLat();
    }
    /* canvas px per design unit: the screen's, never under 2.8 (MapLibre
       draws a hairline street at half strength or more, so too few pixels
       per unit draws the thin streets darker than they print), within what
       a phone's GPU holds */
    function lookRatio(uw, vh) {
      var want = Math.max(2.8, scale * (root.devicePixelRatio || 1));
      var cap = Math.min(LOOK_MAX_DIM / Math.max(uw, vh), Math.sqrt(LOOK_MAX_AREA / (uw * vh)));
      return Math.max(0.08, Math.min(want, cap)) / oz;
    }
    /* phones draw every building once the part drawn is no more ground
       than a neighborhood frame (the budget PHONE_BUILDINGS_FROM sets) */
    function phoneAllBuildings(uw, vh) {
      if (!isPhone() || !cfg.buildingsUrl || d.zoom >= PHONE_BUILDINGS_FROM) return false;
      var f = (uw * vh) / (g.map.w * g.map.h);
      return d.zoom - 0.5 * Math.log(f) / Math.LN2 >= PHONE_BUILDINGS_FROM;
    }
    /* new colors or street weights: the look's picture of the whole map is
       out of date */
    function restyle() {
      if (map) map.setStyle(styleFor());
      if (look.hold) ghost.hidden = true;
    }
    function lookBegin() {
      if (look.hold) return;
      look.hold = true;
      host.setAttribute('data-look', '');
      ghost.hidden = true;
      if (!map || d.state) return;
      try {
        map.redraw();   /* drawn now, so the canvas still holds the picture */
        var src = map.getCanvas(), k = Math.min(1, 2048 / Math.max(src.width, src.height));
        ghost.width = Math.max(1, Math.round(src.width * k));
        ghost.height = Math.max(1, Math.round(src.height * k));
        ghost.getContext('2d').drawImage(src, 0, 0, ghost.width, ghost.height);
        ghost.hidden = false;
      } catch (e) {}
    }
    /* MapLibre sizes each source's tile cache to the map element (5x the
       tiles in view), so the small close-look element would drop the whole
       poster's tiles and going back would fetch them all again (measured:
       99 requests on a 13x19). Up close the cache keeps the whole poster's
       worth; MapLibre's own setting comes back when the look ends. (Its
       per-source caches are style.tileManagers in the vendored 5.24.) A
       look that moves to the print's depth (ozLook) restyles the streets'
       widths, and MapLibre empties a restyled source's cache: coming back
       out reads ~40 of a 13x19's tiles again. */
    var cacheOrig;
    function cacheLevels(lv) {
      try {
        if (cacheOrig === undefined) cacheOrig = map._maxTileCacheZoomLevels;
        var v = lv == null ? cacheOrig : lv, tm = map.style && map.style.tileManagers;
        if (tm) for (var id in tm) if (tm[id] && '_maxTileCacheZoomLevels' in tm[id]) tm[id]._maxTileCacheZoomLevels = v;
        map._maxTileCacheZoomLevels = v;
      } catch (e) {}
    }
    function tilesAt(uw, vh, z) { return (Math.ceil(uw * z / 512) + 1) * (Math.ceil(vh * z / 512) + 1); }
    function tilesOver(uw, vh) { return tilesAt(uw, vh, oz); }
    /* up close, the print's own depth once the part drawn needs no more
       tiles at it than the whole poster needs at its own (a phone's memory
       holds that many; a desktop, twice). Twice as close is too little
       closer for the finer tiles to show, so short of that the whole
       poster's tiles are drawn sharper, with nothing new to fetch (a
       desktop at 4x: the print's depth; at 2x: its own) */
    function ozLook(uw, vh) {
      var full = ozFull(), view = ozView();
      return tilesAt(uw, vh, full) <= tilesAt(g.map.w, g.map.h, view) * (isPhone() ? 1 : 2) ? full : view;
    }
    var lookT = 0;
    function lookSoon() { clearTimeout(lookT); lookT = setTimeout(lookRender, 160); }
    /* draw the part of the map on the stage (plus the margin) sharp */
    function lookRender() {
      clearTimeout(lookT);
      if ((!map && !d.state) || look.m <= 1 || !look.hold || !stageW) return;
      var mx = fitX + g.map.x * scale, my = fitY + g.map.y * scale;
      var a0 = Math.max(0, -mx / scale), a1 = Math.min(g.map.w, (stageW - mx) / scale);
      var b0 = Math.max(0, -my / scale), b1 = Math.min(g.map.h, (stageH - my) / scale);
      if (a1 - a0 < 2 || b1 - b0 < 2) return;   /* the map is off the stage: a close look at the words */
      var w = look.win;
      if (w && Math.abs(w.s - scale) < 1e-6 * scale && w.u0 <= a0 && w.u1 >= a1 && w.v0 <= b0 && w.v1 >= b1) return;
      var mu = (a1 - a0) * LOOK_MARGIN, mv = (b1 - b0) * LOOK_MARGIN;
      var u0 = Math.max(0, a0 - mu), u1 = Math.min(g.map.w, a1 + mu);
      var v0 = Math.max(0, b0 - mv), v1 = Math.min(g.map.h, b1 + mv);
      look.win = { u0: u0, v0: v0, u1: u1, v1: v1, s: scale };
      /* a whole state: its layer is simply drawn again for this window */
      if (d.state) { drawState(); return; }
      /* up close the map reads the print's own depth (ozLook), every
         building and the full elevation (a slow connection's lighter map
         is for the whole poster) */
      var want = ozLook(u1 - u0, v1 - v0), moved = want !== oz;
      if (moved) {
        oz = want;
        map.setMinZoom(P.camZoom({ zoom: 3 }, oz));
        map.setMaxZoom(P.camZoom({ zoom: 16.5 }, oz));
      }
      mapEl.style.width = ((u1 - u0) * oz) + 'px';
      mapEl.style.height = ((v1 - v0) * oz) + 'px';
      mapEl.style.left = (u0 * scale) + 'px';
      mapEl.style.top = (v0 * scale) + 'px';
      mapEl.style.transform = 'scale(' + (scale / oz) + ')';
      cacheLevels(Math.ceil(tilesOver(g.map.w, g.map.h) / tilesOver(u1 - u0, v1 - v0)) + 5);
      map.resize();
      map.jumpTo({ center: geoAt((u0 + u1) / 2, (v0 + v1) / 2), zoom: P.camZoom(d, oz), bearing: d.bearing || 0 });
      map.setPixelRatio(lookRatio(u1 - u0, v1 - v0));
      var all = phoneAllBuildings(u1 - u0, v1 - v0);
      if (moved || all !== look.bldAll || depth.slow) { look.bldAll = all; map.setStyle(styleFor()); }
      map.redraw();
    }
    /* back to the whole map at the design's camera */
    var quietT = 0;
    function lookEnd() {
      clearTimeout(lookT);
      if (!look.hold) return;
      look.win = null;
      look.quiet = true;
      look.hold = false;
      /* back to the whole poster's own depth */
      var back = !!map && !d.state && ozView() !== oz;
      if (back) {
        oz = ozView();
        map.setMinZoom(P.camZoom({ zoom: 3 }, oz));
        map.setMaxZoom(P.camZoom({ zoom: 16.5 }, oz));
      }
      mapEl.style.width = (g.map.w * oz) + 'px';
      mapEl.style.height = (g.map.h * oz) + 'px';
      mapEl.style.left = '0px';
      mapEl.style.top = '0px';
      mapEl.style.transform = 'scale(' + (scale / oz) + ')';
      if (map && !d.state) {
        cacheLevels(null);
        map.resize();
        map.jumpTo({ center: [d.lon, d.lat], zoom: P.camZoom(d, oz), bearing: d.bearing || 0 });
        map.setPixelRatio(previewRatio());
        if (back || look.bldAll || depth.slow) { look.bldAll = false; map.setStyle(styleFor()); }
        map.redraw();
      }
      ghost.hidden = true;
      host.removeAttribute('data-look');
      /* MapLibre resizes again on its own ~50 ms later (its ResizeObserver),
         firing move events that aren't the customer's */
      clearTimeout(quietT);
      quietT = setTimeout(function () { look.quiet = false; }, 200);
    }
    /* straight back to the whole poster (the poster changed shape, or the
       map is about to move) */
    function lookReset() {
      stopTween();
      look.m = 1;
      fitStage();
      lookEnd();
      fitStage();
    }
    /* a gesture or a zoom ended: draw the part on the stage sharp, or at
       the whole poster go back to the plain view */
    function lookSettle() {
      flushLayout();
      if (look.m <= 1.001) { if (look.hold) lookReset(); return; }
      lookRender();
    }
    var raf = root.requestAnimationFrame ? root.requestAnimationFrame.bind(root) : function (f) { return setTimeout(f, 16); };
    var caf = root.cancelAnimationFrame ? root.cancelAnimationFrame.bind(root) : clearTimeout;
    var layoutRaf = 0, tweenRaf = 0;
    function layoutSoon() { if (!layoutRaf) layoutRaf = raf(function () { layoutRaf = 0; fitStage(); }); }
    function flushLayout() { if (layoutRaf) { caf(layoutRaf); layoutRaf = 0; fitStage(); } }
    function stopTween() { if (tweenRaf) { caf(tweenRaf); tweenRaf = 0; } }
    function posterPt(px, py) { return { x: (px - fitX) / scale, y: (py - fitY) / scale }; }
    /* m times closer, with poster point p0 under stage point (px, py) */
    function zoomAround(m, px, py, p0) {
      m = clampM(m);
      if (m > 1) lookBegin();
      look.m = m;
      var s = fitScale * m;
      look.cx = p0.x + (roomX - px) / s;
      look.cy = p0.y + (roomY - py) / s;
    }
    /* the buttons, a double tap: a quick ease to m times closer around
       (px, py), the stage's middle when not given */
    function lookZoomTo(m1, px, py) {
      stopTween(); flushLayout();
      m1 = clampM(m1);
      var m0 = look.m;
      if (Math.abs(m1 - m0) < 1e-3) { lookSettle(); return; }
      if (px == null) { px = roomX; py = roomY; }
      var p0 = posterPt(px, py), c0x = look.cx, c0y = look.cy, home = m1 <= 1;
      var t0 = Date.now();
      if (!home) lookBegin();
      (function step() {
        var t = Math.min(1, (Date.now() - t0) / 240), k = 1 - Math.pow(1 - t, 3);
        var m = Math.exp(Math.log(m0) + (Math.log(m1) - Math.log(m0)) * k);
        if (home) { look.m = Math.max(1, m); look.cx = c0x + (g.W / 2 - c0x) * k; look.cy = c0y + (g.H / 2 - c0y) * k; }
        else zoomAround(m, px, py, p0);
        fitStage();
        if (t < 1) tweenRaf = raf(step); else { tweenRaf = 0; lookSettle(); }
      })();
    }
    function setLocked(on) {
      look.locked = !!on;
      if (on) host.setAttribute('data-locked', ''); else host.removeAttribute('data-locked');
      var lab = on ? 'Unlock the map' : 'Lock the map';
      lockBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
      lockBtn.setAttribute('aria-label', lab);
      lockBtn.title = lab;
      lockBtn.innerHTML = icon(on ? I.lock : I.unlock, 18);
      mapGestures();
      look.hinted = false;
      if (on) toast('Map locked. Zoom in to see your poster up close.', 3600);
      else { toast(''); if (look.m > 1) lookZoomTo(1); }
      lookButtons();
    }
    /* the map's own gestures: off while it is locked */
    function mapGestures() {
      if (!map) return;
      var on = !look.locked;
      ['dragPan', 'scrollZoom', 'touchZoomRotate', 'doubleClickZoom', 'keyboard', 'boxZoom'].forEach(function (k) {
        if (map[k]) map[k][on ? 'enable' : 'disable']();
      });
      if (on) {
        map.touchZoomRotate.disableRotation();
        if (map.keyboard.disableRotation) map.keyboard.disableRotation();
      }
    }
    lockBtn.addEventListener('click', function () { setLocked(!look.locked); });
    wholeBtn.addEventListener('click', function () { lookZoomTo(1); });
    function lockHint() {
      if (look.hinted) return;
      look.hinted = true;
      toast('The map is locked. ' + (isTouch() ? 'Tap' : 'Click') + ' the padlock to move it.', 3200);
    }
    var lookState = '';
    function lookButtons() {
      var top = look.m >= lookMax() - 1e-3, st = [look.locked, look.m > 1, top].join();
      if (st === lookState) return;
      lookState = st;
      var zi = q('[data-z="in"]', zoomBox), zo = q('[data-z="out"]', zoomBox);
      zi.disabled = look.locked && top;
      zo.disabled = look.locked && look.m <= 1;
      zi.setAttribute('aria-label', look.locked ? 'Zoom in on the poster' : 'Zoom in');
      zo.setAttribute('aria-label', look.locked ? 'Zoom out on the poster' : 'Zoom out');
      wholeBtn.hidden = !(look.m > 1);
    }
    /* gestures while locked: drag to move the poster up close, pinch or
       the wheel to zoom, a double tap (with a mouse, a click at the whole
       poster) for twice as close */
    var pts = {}, gest = null, lastTap = null;
    function stagePt(e) { var r = stage.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; }
    function gestStart() {
      flushLayout();
      var ids = Object.keys(pts);
      if (ids.length >= 2) {
        var a = pts[ids[0]], b = pts[ids[1]];
        gest = { pinch: true, d0: Math.max(10, Math.hypot(a.x - b.x, a.y - b.y)), m0: look.m, p0: posterPt((a.x + b.x) / 2, (a.y + b.y) / 2), moved: true };
      } else if (ids.length === 1) {
        var p = pts[ids[0]];
        gest = { pinch: false, x0: p.x, y0: p.y, cx0: look.cx, cy0: look.cy, moved: false };
      } else gest = null;
    }
    lookEl.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      e.preventDefault();
      stopTween();
      try { lookEl.setPointerCapture(e.pointerId); } catch (x) {}
      pts[e.pointerId] = stagePt(e);
      gestStart();
    });
    lookEl.addEventListener('pointermove', function (e) {
      if (!pts[e.pointerId] || !gest) return;
      pts[e.pointerId] = stagePt(e);
      var ids = Object.keys(pts);
      if (gest.pinch && ids.length >= 2) {
        var a = pts[ids[0]], b = pts[ids[1]];
        var dist = Math.max(10, Math.hypot(a.x - b.x, a.y - b.y));
        zoomAround(gest.m0 * dist / gest.d0, (a.x + b.x) / 2, (a.y + b.y) / 2, gest.p0);
        layoutSoon();
      } else if (!gest.pinch) {
        var p = pts[ids[0]], dx = p.x - gest.x0, dy = p.y - gest.y0;
        if (!gest.moved && Math.abs(dx) + Math.abs(dy) < 6) return;
        gest.moved = true;
        if (look.m <= 1) { lockHint(); return; }
        lookEl.classList.add('kpm-grabbing');
        look.cx = gest.cx0 - dx / scale;
        look.cy = gest.cy0 - dy / scale;
        layoutSoon();
      }
    });
    function pointerEnd(e) {
      var p = pts[e.pointerId]; if (!p) return;
      delete pts[e.pointerId];
      var was = gest;
      if (Object.keys(pts).length) { gestStart(); return; }
      gest = null;
      lookEl.classList.remove('kpm-grabbing');
      if (was && !was.pinch && !was.moved && e.type === 'pointerup') {
        if (e.pointerType === 'mouse' && look.m <= 1) { lookZoomTo(2, p.x, p.y); return; }
        var t = Date.now();
        if (lastTap && t - lastTap.t < 320 && Math.abs(p.x - lastTap.x) + Math.abs(p.y - lastTap.y) < 40) {
          lastTap = null;
          lookZoomTo(look.m >= lookMax() - 1e-3 ? 1 : look.m * 2, p.x, p.y);
          return;
        }
        lastTap = { t: t, x: p.x, y: p.y };
      }
      lookSettle();
    }
    lookEl.addEventListener('pointerup', pointerEnd);
    lookEl.addEventListener('pointercancel', pointerEnd);
    var wheelDone = debounce(lookSettle, 200);
    lookEl.addEventListener('wheel', function (e) {
      e.preventDefault();
      stopTween(); flushLayout();
      var p = stagePt(e);
      var dy = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? stageH : 1);
      zoomAround(look.m * Math.exp(-dy * 0.0022), p.x, p.y, posterPt(p.x, p.y));
      layoutSoon();
      wheelDone();
    }, { passive: false });

    /* ── Place: search, popular places, the place on the poster ─────── */
    var input = q('.kpm-citypick .kpm-searchbox input'), results = q('.kpm-results');
    var chips = q('.kpm-chips');
    POPULAR.forEach(function (p, i) {
      chips.appendChild(h('button', { type: 'button', class: 'kpm-chip', 'data-pop': i, 'aria-pressed': 'false' },
        esc(p.title) + ' <small>' + esc(p.tag) + '</small>'));
    });
    chips.addEventListener('click', function (e) {
      var b = e.target.closest('[data-pop]'); if (!b) return;
      var p = POPULAR[+b.getAttribute('data-pop')];
      pickPlace({ title: p.title, sub: p.sub, lat: p.lat, lon: p.lon, zoom: p.zoom, label: p.title + ', ' + p.sub, cc: p.cc, own: p.own });
    });
    function chipsState() {
      qa('[data-pop]', chips).forEach(function (b) {
        var p = POPULAR[+b.getAttribute('data-pop')];
        var on = p.title === d.title && Math.abs(p.lat - home.lat) < 0.5 && Math.abs(p.lon - home.lon) < 0.5;
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
    function placeName() { return d.place || [d.title, d.sub].filter(Boolean).join(', '); }
    /* a state's size, as a fact (its table row, once loaded) */
    function stateMeta() {
      var st = statesBy && statesBy[d.state];
      return st ? Math.round(st.w_km * 0.6214) + ' × ' + Math.round(st.h_km * 0.6214) + ' miles' : 'every road in the state';
    }
    /* how much ground the map shows, as a fact: the map's width at its zoom */
    function across() {
      var mpu = 2 * Math.PI * 6378137 / (512 * Math.pow(2, d.zoom));
      var mi = g.map.w * mpu * Math.cos(d.lat * Math.PI / 180) / 1609.344;
      return (mi < 10 ? mi.toFixed(1) : String(Math.round(mi))) + ' miles across';
    }
    var placeQueued = false;
    function placeSoon() {
      if (placeQueued) return;
      placeQueued = true;
      (root.requestAnimationFrame || setTimeout)(function () {
        placeQueued = false;
        placeNameEl.textContent = placeName();
        placeMetaEl.textContent = P.fmtCoords(P.aimLat(d), d.lon) + ' · ' + (d.state ? stateMeta() : across());
      });
    }
    var seq = 0, rows = [];
    var search = debounce(function () {
      var text = input.value.trim(), my = ++seq;
      if (text.length < 2) { results.hidden = true; return; }
      geocode(text).then(function (list) {
        if (my !== seq) return;
        rows = list;
        results.innerHTML = list.length ? list.map(function (r, i) {
          return '<li role="option"><button type="button" data-row="' + i + '">' + esc(r.title) + (r.detail ? ' <span>' + esc(r.detail) + '</span>' : '') + '</button></li>';
        }).join('') : '<li class="kpm-none">No places found.</li>';
        results.hidden = false;
      });
    }, 220);
    input.addEventListener('input', search);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { var f = results.querySelector('[data-row]'); if (f) f.click(); e.preventDefault(); }
      if (e.key === 'Escape') results.hidden = true;
    });
    results.addEventListener('click', function (e) {
      var b = e.target.closest('[data-row]'); if (!b) return;
      var r = rows[+b.getAttribute('data-row')];
      results.hidden = true;
      input.value = '';
      if (r.gid && typeof r.lat !== 'number') placeDetails(r).then(function (full) { if (full) pickPlace(full); });
      else pickPlace(r);
    });
    doc.addEventListener('click', function (e) { if (!results.hidden && !e.target.closest('.kpm-search')) results.hidden = true; });
    /* a link's city alone (?city=Chicago, no lat and lon — linkOpening's
       find): looked up as a typed search would be and the first row picked,
       the way Enter picks it. The link's words stand while the lookup runs
       and stay if nothing is found. A place the customer picks first wins
       (pickPlace counts the asks), so a slow lookup never yanks the map
       from under them. A second line the link gave narrows the search and
       is kept as written; a search it only confuses ("Chicago, Our town")
       is tried again on the city alone. */
    var openAsk = 0;
    function openPlace(find) {
      var my = ++openAsk;
      var text = find.city + (find.sub ? ', ' + find.sub : '');
      geocode(text).then(function (list) {
        if (list.length || !find.sub) return list;
        return geocode(find.city);
      }).then(function (list) {
        var r = list[0];
        if (!r || my !== openAsk) return null;
        return r.gid && typeof r.lat !== 'number' ? placeDetails(r) : r;
      }).then(function (full) {
        if (!full || my !== openAsk) return;
        if (find.zoom != null) full = Object.assign({}, full, { zoom: find.zoom, bbox: null });
        pickPlace(full);
        if (find.sub && d.sub !== find.sub) { d.sub = find.sub.slice(0, 44); syncWords(); drawOver(); renderState(); saveSoon(); }
      }).catch(function () {});
    }
    function pickPlace(r) {
      ++openAsk;
      if (d.state) leaveState(false);
      d.title = r.title;
      d.sub = r.sub || '';
      d.place = r.label || r.title;
      d.tagAuto = true;
      setLocal(r);
      syncWords();
      goTo(r.lat, r.lon, r.zoom || Z_CITY, r.bbox);
      home = homeHere();
      /* a marker moves to the newly picked place */
      if (d.marker) d.marker = { icon: d.marker.icon, lat: r.lat, lon: r.lon };
      d.tag = autoTag(); syncWords(true); drawOver();
      markerButtons(); chipsState(); fontCards(); renderState(); placeSoon();
      if (P.font(d.font).local || d.local) loadThen();
      saveSoon();
    }
    /* LOCAL LETTERING (KRPoster.LOCAL): a place in a country with a script
       of its own carries its own spelling (d.local), for the Words step's
       local card. A local lettering that is on stays on where the new place
       writes in it (Tokyo → Kyoto) and goes back to the Latin face it came
       from where it doesn't (Tokyo → Paris): never a script the customer
       didn't pick. The place's own name is asked of the geocoder once, in
       its language; the country's comes from the browser. */
    var localAsk = 0;
    function setLocal(r) {
      var L = P.localFor(r.cc), was = P.font(d.font), my = ++localAsk;
      d.local = L ? { cc: String(r.cc).toUpperCase(), title: '', sub: P.localCountry(r.cc, L), en: [d.title, d.sub] } : null;
      if (was && was.local && (!L || L.key !== was.key)) d.font = latinFont;
      if (!L) return;
      if (r.own) ownTitle(d.local, r.own);
      if (d.font === L.key) toLocalWords();
      P.localSheet(L);
      if (d.local.title) return;
      ownName(r, L).then(function (name) {
        var lc = d.local, sub = lc && lc.sub;
        if (my !== localAsk || !name || !lc) return;
        ownTitle(lc, name);
        if (d.font === L.key) {
          if (d.title === lc.en[0]) d.title = name;
          if (d.sub === sub) d.sub = lc.sub;
          syncWords(); renderState(); loadThen();
        }
        fontCards(); saveSoon();
      });
    }
    /* the place's own spelling found: a city that is its country's own name
       (香港, 澳門) letters alone, without a second line saying it again */
    function ownTitle(lc, name) {
      lc.title = name;
      if (lc.sub === name) lc.sub = '';
    }
    /* the place's own name in its language: Stadia's place details for the
       picked row (an address or a landmark names its city), or Photon's
       local name for the same OSM object; '' when neither has it written in
       the script — Taipei comes back romanized ("T'ai-pei"), and its city
       region "Taipei City" (台北市) stands in */
    function ownName(r, L) {
      if (r.gid && cfg.stadiaKey) {
        return fetch(STADIA + 'place_details?lang=' + L.geo + '&api_key=' + encodeURIComponent(cfg.stadiaKey) + '&ids=' + encodeURIComponent(r.gid))
          .then(function (res) { return res.ok ? res.json() : null; })
          .then(function (j) {
            var p = (j && j.features && j.features[0] && j.features[0].properties) || {};
            var wof = (p.context && p.context.whosonfirst) || {}, layer = p.layer || r.layer || '';
            if (/address|street|poi|venue/.test(layer)) return P.localName(wof.locality && wof.locality.name, L, false);
            var n = P.localName(p.name, L, /county|borough|localadmin|region/.test(layer));
            if (!n && r.region && r.region.toLowerCase() === (r.title + ' city').toLowerCase()) n = P.localName(wof.region && wof.region.name, L, true);
            return n;
          }, function () { return ''; });
      }
      if (r.osm) {
        /* the same OSM object, else — for a picked town or city — the
           nearest one within 25 km (the two searches can rank differently) */
        var placeKind = /city|town|village|district|locality/;
        return fetch('https://photon.komoot.io/api/?limit=6&lang=default&q=' + encodeURIComponent(r.title + (r.sub ? ', ' + r.sub : '')))
          .then(function (res) { return res.ok ? res.json() : null; })
          .then(function (j) {
            var list = (j && j.features) || [], best = null, bestKm = 25;
            list.forEach(function (f) {
              var p = f.properties || {}, c = (f.geometry && f.geometry.coordinates) || [];
              if (p.osm_type + p.osm_id === r.osm) { best = f; bestKm = -1; return; }
              if (bestKm < 0 || !placeKind.test(r.kind || '') || !placeKind.test(p.type || '') || typeof c[0] !== 'number') return;
              var km = Math.hypot((c[0] - r.lon) * Math.cos(r.lat * Math.PI / 180), c[1] - r.lat) * 111.2;
              if (km < bestKm) { best = f; bestKm = km; }
            });
            return best ? P.localName(best.properties.name, L, true) : '';
          }, function () { return ''; });
      }
      return Promise.resolve('');
    }
    /* Stadia (the account the book studio uses) when a key is set; Photon
       (komoot's free OSM geocoder) otherwise and as the fallback.
       Stadia AUTOCOMPLETE v2, not v1: v1 costs 20 credits a request and a
       typed search fires ~4 of them; v2 costs 1 but returns names only, so
       the pick is looked up once more (place details, 20 credits) — ~24
       credits a search instead of ~80 (Stadia pricing, 2026-10-24). */
    var STADIA = 'https://api.stadiamaps.com/geocoding/v2/';
    function geocode(text) {
      var key = cfg.stadiaKey;
      var stadia = key ? fetch(STADIA + 'autocomplete?size=6&lang=en&api_key=' +
        encodeURIComponent(key) + '&text=' + encodeURIComponent(text)).then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) { return ((j && j.features) || []).map(fromStadia).filter(Boolean); }) : Promise.resolve([]);
      return stadia.then(function (list) { return list.length ? list : photon(text); }, function () { return photon(text); });
    }
    function zoomFor(layer) {
      return /poi|venue|address|street/.test(layer) ? Z_STREET : /neighbourhood|borough|microhood|macrohood/.test(layer) ? Z_HOOD
        : /locality|localadmin/.test(layer) ? Z_CITY : /county|region|macroregion|country/.test(layer) ? Z_REGION : Z_TOWN;
    }
    function fromStadia(f) {
      var p = f.properties || {};
      if (!p.gid || !p.name) return null;
      return { title: p.name, detail: p.coarse_location || '', label: [p.name, p.coarse_location].filter(Boolean).join(', '),
        gid: p.gid, layer: p.layer || '' };
    }
    /* the picked row's coordinates, bounds and its state / country: the
       second line names the STATE for a US place (Kevin's 2025 posters:
       "Chicago / Illinois") and the country elsewhere */
    function placeDetails(r) {
      return fetch(STADIA + 'place_details?lang=en&api_key=' + encodeURIComponent(cfg.stadiaKey) + '&ids=' + encodeURIComponent(r.gid))
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (j) {
          var f = j && j.features && j.features[0];
          var c = f && f.geometry && f.geometry.coordinates;
          if (!c || typeof c[0] !== 'number') return null;
          var ctx = (f.properties && f.properties.context) || {}, wof = ctx.whosonfirst || {};
          var region = wof.region && wof.region.name, country = wof.country && wof.country.name;
          var layer = (f.properties && f.properties.layer) || r.layer;
          var useBox = /locality|localadmin|borough|neighbourhood/.test(layer) && (f.bbox || j.bbox);
          /* an address or a landmark is a spot IN a city: the poster's title
             is the city ("Washington", not "1600 Pennsylvania Avenue NW") */
          var city = wof.locality && wof.locality.name;
          var title = (/address|street|poi|venue/.test(layer) && city) ? city : r.title;
          return { title: title, sub: (ctx.iso_3166_a2 === 'US' ? region : country) || country || '', label: r.label,
            lat: c[1], lon: c[0], zoom: zoomFor(layer), bbox: useBox ? (f.bbox || j.bbox) : null,
            cc: ctx.iso_3166_a2 || '', gid: r.gid, layer: layer, region: region || '' };
        }, function () { return null; });
    }
    function photon(text) {
      return fetch('https://photon.komoot.io/api/?limit=6&lang=en&q=' + encodeURIComponent(text))
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) {
          return ((j && j.features) || []).map(function (f) {
            var c = (f.geometry && f.geometry.coordinates) || [], p = f.properties || {};
            if (typeof c[0] !== 'number') return null;
            var type = p.type || '';
            var zoom = /house|street/.test(type) ? Z_STREET : /district|locality/.test(type) ? Z_HOOD
              : /city/.test(type) ? Z_CITY : /county|state|country/.test(type) ? Z_REGION : Z_TOWN;
            var name = p.name || p.street || '';
            var detail = [p.city !== name ? p.city : '', p.state, p.country].filter(Boolean).join(', ');
            var ext = p.extent; /* [minLon, maxLat, maxLon, minLat] */
            var bbox = (/city|district/.test(type) && ext && ext.length === 4) ? [ext[0], ext[3], ext[2], ext[1]] : null;
            return { title: name, sub: p.country || '', detail: detail, label: [name, detail].filter(Boolean).join(', '),
              lat: c[1], lon: c[0], zoom: zoom, bbox: bbox, cc: p.countrycode || '', osm: p.osm_type && p.osm_id ? p.osm_type + p.osm_id : '', kind: type };
          }).filter(Boolean);
        }, function () { return []; });
    }

    /* ── a whole state ──────────────────────────────────────────────── */
    /* The fifty states (assets/kr-poster-states.json: code, name, the
       centroid the coordinates line shows, the projected shape's aspect,
       which orientation fits it better, an outline for the picker's icons)
       are fetched when first needed. */
    var kindSeg = q('.kpm-kind'), cityPick = q('.kpm-citypick'), statePick = q('.kpm-statepick');
    /* the door opens once the layers archive answers: until it is online
       (or while the worker is down) the Place step is a city's alone. A
       design that opens on a state keeps the door, the way back to a city. */
    if (kindSeg && !d.state) {
      kindSeg.hidden = true;
      layersArchive().then(function () { kindSeg.hidden = false; }, function () {});
    }
    var statesBox = q('.kpm-states'), stateIn = q('.kpm-statepick input');
    var statesP = null, statesBy = null, statesList = null;
    function states() {
      if (!statesP) {
        statesP = fetch(cfg.statesUrl, { credentials: 'omit' }).then(function (r) {
          if (!r.ok) throw new Error('states ' + r.status);
          return r.json();
        }).then(function (list) {
          statesList = list; statesBy = {};
          list.forEach(function (st) { statesBy[st.code] = st; });
          buildStates();
          return list;
        }, function (e) {
          statesP = null;
          statesBox.innerHTML = '<span class="kpm-none">The states could not load. Please refresh the page.</span>';
          throw e;
        });
      }
      return statesP;
    }
    function buildStates() {
      if (!statesList) return;
      statesBox.innerHTML = statesList.map(function (st) {
        /* the outline is stored stretched to a square; the icon keeps the
           state's own shape */
        var path = st.rings.map(function (r) {
          return 'M' + r.map(function (pt, i) { return (i ? 'L' : '') + pt[0] + ' ' + Math.round(pt[1] / st.aspect); }).join('') + 'Z';
        }).join('');
        return '<button type="button" class="kpm-statebtn" role="option" data-state="' + st.code + '" data-name="' + esc(st.name.toLowerCase()) + '" aria-pressed="false">' +
          '<svg viewBox="0 0 1000 ' + Math.round(1000 / st.aspect) + '" aria-hidden="true" focusable="false"><path d="' + path + '"/></svg>' +
          '<span>' + esc(st.name) + '</span></button>';
      }).join('');
      stateButtons();
      filterStates();
    }
    function stateButtons() {
      qa('[data-state]', statesBox).forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-state') === d.state ? 'true' : 'false'); });
      if (kindSeg) qa('[data-kind]', kindSeg).forEach(function (b) { b.setAttribute('aria-pressed', (b.getAttribute('data-kind') === 'state') === pickingState ? 'true' : 'false'); });
    }
    function filterStates() {
      var text = (stateIn.value || '').trim().toLowerCase();
      qa('[data-state]', statesBox).forEach(function (b) {
        b.hidden = !!text && b.getAttribute('data-name').indexOf(text) < 0 && b.getAttribute('data-state').toLowerCase() !== text;
      });
    }
    /* which picker the Place step shows (a state is picked from the second) */
    var pickingState = !!d.state;
    function showPicker(state, focus) {
      pickingState = state;
      cityPick.hidden = state;
      statePick.hidden = !state;
      q('.kpm-tipcity', panePlace).hidden = state;
      q('.kpm-tipstate', panePlace).hidden = !state;
      q('.kpm-lede', panePlace).textContent = state ? LEDE_STATE : LEDE_CITY;
      stateButtons();
      if (state) states().then(function () { if (focus && pickingState && !isPhone()) stateIn.focus(); }, function () {});
    }
    if (kindSeg) kindSeg.addEventListener('click', function (e) {
      var b = e.target.closest('[data-kind]'); if (!b) return;
      var toState = b.getAttribute('data-kind') === 'state';
      if (!toState && d.state) leaveState(true);
      showPicker(toState, true);
    });
    stateIn.addEventListener('input', filterStates);
    stateIn.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      var first = qa('[data-state]', statesBox).filter(function (b) { return !b.hidden; })[0];
      if (first) { pickState(first.getAttribute('data-state')); e.preventDefault(); }
    });
    statesBox.addEventListener('click', function (e) {
      var b = e.target.closest('[data-state]'); if (b) pickState(b.getAttribute('data-state'));
    });
    /* the city design to come back to when the state door closes */
    var cityHome = null;
    function pickState(code) {
      states().then(function () {
        var st = statesBy[code];
        if (!st) return;
        if (!d.state) cityHome = { title: d.title, sub: d.sub, place: d.place, lat: d.lat, lon: d.lon, zoom: d.zoom, orient: d.orient, marker: d.marker,
          local: d.local, font: d.font };
        var was = d.state;
        d.state = code;
        d.title = st.name; d.sub = 'United States'; d.place = st.name + ', United States';
        /* a state letters in Latin: a city's own script waits with the city */
        d.local = null;
        if (P.font(d.font).local) d.font = latinFont;
        d.lat = st.lat; d.lon = st.lon;
        d.marker = null;
        d.tagAuto = true; d.tag = autoTag();
        /* the state's shape suggests the orientation (the Size step can
           still turn it) */
        d.orient = st.orient;
        if (!was) enterStateMode();
        syncWords(); fontCards(); orientButtons(); renderOptions(); stateButtons(); markerButtons();
        applyGeom(); drawOver(); renderState(); placeSoon(); saveSoon(); thumbs.request();
      }, function () {});
    }
    /* the maker in state mode: the map hidden, the layer's canvas shown, the
       Marker step aside, the lock on (the only zoom is the close look) */
    function enterStateMode() {
      host.setAttribute('data-state', '');
      mapEl.hidden = true;
      stateCv.hidden = false;
      ghost.hidden = true;
      if (look.hold) lookReset();
      look.locked = true;
      host.setAttribute('data-locked', '');
      if (map) ['dragPan', 'scrollZoom', 'touchZoomRotate', 'doubleClickZoom', 'keyboard', 'boxZoom'].forEach(function (k) { if (map[k]) map[k].disable(); });
      renumber();
      if (host.getAttribute('data-tab') === 'marker') showTab('words');
      else renderFoot(stepIndex(host.getAttribute('data-tab')));
      lookButtons();
      pickingState = true;
    }
    /* back to a city: the one the state door was opened from, or where the
       state was */
    function leaveState(goHome) {
      if (!d.state) return;
      var st = statesBy && statesBy[d.state];
      d.state = '';
      host.removeAttribute('data-state');
      stateCv.hidden = true;
      mapEl.hidden = false;
      if (look.hold) lookReset();
      look.locked = false;
      host.removeAttribute('data-locked');
      veil.innerHTML = '<span>Loading the map&hellip;</span>';
      veil.hidden = !!map;
      if (goHome && cityHome) {
        d.title = cityHome.title; d.sub = cityHome.sub; d.place = cityHome.place; d.orient = cityHome.orient; d.marker = cityHome.marker;
        d.lat = cityHome.lat; d.lon = cityHome.lon; d.zoom = cityHome.zoom;
        d.local = cityHome.local || null; d.font = cityHome.font || d.font;
      } else {
        /* no city to go back to (the maker opened on the state): the map
           opens on the state's own middle, wide, with its words kept */
        d.zoom = Z_REGION;
      }
      d.tag = autoTag();
      home = homeHere();
      cityHome = null;
      renumber();
      renderFoot(stepIndex(host.getAttribute('data-tab')));
      if (!map) startMap();
      else { setLocked(false); map.jumpTo({ center: [d.lon, d.lat], zoom: P.camZoom(d, oz), bearing: 0 }); }
      syncWords(); fontCards(); orientButtons(); renderOptions(); stateButtons(); markerButtons(); chipsState();
      applyGeom(); drawOver(); renderState(); placeSoon(); saveSoon(); thumbs.request();
    }
    /* THE LAYERS: one PMTiles archive (the section's "State map layers"
       URL; docs/poster-maker/state/pack-web.py) holds every state's
       pre-rendered layers as WebP tiles — a class layer per orientation and
       line class (light lines on a dark ground are drawn heavier) and the
       state's mask — and, in its metadata, where each one is and how it
       sits. The krmaps-api worker serves it in byte ranges like the map
       tiles. A layer is recolored into the design's colours by
       KRPoster.stateRecolor and laid on the poster by KRPoster.statePlace;
       each is fetched once per key. */
    var layers = {}, archive = null, archiveMeta = null;
    function layerKey() { return d.state.toLowerCase() + '-' + d.orient + '-' + P.stateClass(P.theme(d.theme)); }
    function layersArchive() {
      if (!archiveMeta) {
        if (!cfg.stateLayersUrl) return Promise.reject(new Error('no state layers'));
        /* (the PMTiles reader may still be on its way: needLibs) */
        archiveMeta = needLibs(['pmtiles']).then(function () {
          archive = new root.pmtiles.PMTiles(cfg.stateLayersUrl);
          return archive.getMetadata();
        }).then(function (m) {
          if (!m || m.kr_state_layers !== 1 || !m.states) throw new Error('not a state layers archive');
          return m;
        });
        /* a failed read is tried again next time it is asked for */
        archiveMeta.then(null, function () { archiveMeta = null; });
      }
      return archiveMeta;
    }
    /* one layer image out of the archive: WebP bytes, decoded as data (no
       colour management: the channels are shares, not colours) */
    function tileImage(t) {
      return archive.getZxy(t[0], t[1], t[2]).then(function (r) {
        if (!r || !r.data) throw new Error('a state layer is missing from the archive');
        var blob = new Blob([r.data], { type: 'image/webp' });
        var viaImage = function () {
          return new Promise(function (ok, no) {
            var url = URL.createObjectURL(blob), im = new Image();
            im.onload = function () { URL.revokeObjectURL(url); ok(im); };
            im.onerror = function () { URL.revokeObjectURL(url); no(new Error('a state layer would not decode')); };
            im.src = url;
          });
        };
        if (!root.createImageBitmap) return viaImage();
        return root.createImageBitmap(blob, { colorSpaceConversion: 'none', premultiplyAlpha: 'none' }).then(null, viaImage);
      });
    }
    function imageData(im) {
      var c = doc.createElement('canvas');
      c.width = im.naturalWidth || im.width; c.height = im.naturalHeight || im.height;
      var cx = c.getContext('2d', { willReadFrequently: true });
      cx.drawImage(im, 0, 0);
      if (im.close) im.close();
      return cx.getImageData(0, 0, c.width, c.height);
    }
    function stateLayer() {
      var key = layerKey();
      if (!layers[key]) {
        var code = d.state.toLowerCase(), orient = d.orient, cls = P.stateClass(P.theme(d.theme));
        var p = layersArchive().then(function (m) {
          var e = m.states[code] && m.states[code][orient];
          if (!e) throw new Error('no layer for ' + code + ' ' + orient);
          return Promise.all([tileImage(e[cls]), tileImage(e.mask)]).then(function (im) {
            var lay = {
              meta: { image_in: { px_w: e.px[0], px_h: e.px[1] }, hair_alpha: m.baked_share,
                state_px: { left: e.state[0], top: e.state[1], w: e.state[2], h: e.state[3] } },
              cls: imageData(im[0]), mask: imageData(im[1])
            };
            if (lay.cls.width !== e.px[0] || lay.cls.height !== e.px[1]) throw new Error('a state layer is the wrong size');
            layers[key] = lay;
            return lay;
          });
        }).then(null, function (e) { delete layers[key]; throw e; });
        layers[key] = p;
      }
      return layers[key];
    }
    function layerReady() { var l = layers[layerKey()]; return l && !l.then ? l : null; }
    /* the layer in the design's colours, at the layer's own resolution (one
       at a time: a theme change recolors it, ~50 ms for a 13x19) */
    var composed = { key: '', cv: null, meta: null };
    function composeState(lay) {
      var t = P.theme(d.theme), key = layerKey() + '|' + t.key + '|' + d.share;
      if (composed.key === key) return composed;
      var w = lay.cls.width, hh = lay.cls.height;
      var cv = composed.cv || doc.createElement('canvas');
      cv.width = w; cv.height = hh;
      var cx = cv.getContext('2d');
      cx.putImageData(P.stateRecolor(lay.cls, lay.mask, t, { share: d.share, baked: lay.meta.hair_alpha }, cx.createImageData(w, hh)), 0, 0);
      composed = { key: key, cv: cv, meta: lay.meta };
      return composed;
    }
    var STATE_MAX_DIM = 4096, STATE_MAX_AREA = 12e6, loadingKey = '';
    /* draw the state's layer into its canvas: the whole map rectangle, or
       in a close look the part on the stage (look.win), never finer than
       the layer itself or than a phone's GPU holds */
    function drawState() {
      if (!d.state) return;
      var lay = layerReady();
      if (!lay) {
        var key = layerKey();
        if (loadingKey !== key) {
          loadingKey = key;
          veil.innerHTML = '<span>Drawing every road in ' + esc(d.title || 'the state') + '&hellip;</span>';
          veil.hidden = false;
          stateLayer().then(function () {
            if (layerKey() !== key) return;
            loadingKey = '';
            veil.hidden = true;
            drawState();
            thumbs.request();
          }, function () {
            if (layerKey() !== key) return;
            loadingKey = '';
            veil.innerHTML = '<span>' + esc(d.title || 'This state') + ' could not load. Please try again in a moment.</span>';
          });
        }
        drawStateOutline();
        return;
      }
      var cm = composeState(lay), pl = P.statePlace(g, cm.meta);
      var w = look.win || { u0: 0, v0: 0, u1: g.map.w, v1: g.map.h };
      var uw = w.u1 - w.u0, vh = w.v1 - w.v0;
      var want = scale * (root.devicePixelRatio || 1);
      var k = Math.min(want, 1 / pl.k, STATE_MAX_DIM / Math.max(uw, vh), Math.sqrt(STATE_MAX_AREA / (uw * vh)));
      var cw = Math.max(1, Math.round(uw * k)), ch = Math.max(1, Math.round(vh * k));
      if (stateCv.width !== cw) stateCv.width = cw;
      if (stateCv.height !== ch) stateCv.height = ch;
      stateCv.style.left = (w.u0 * scale) + 'px';
      stateCv.style.top = (w.v0 * scale) + 'px';
      stateCv.style.width = (uw * scale) + 'px';
      stateCv.style.height = (vh * scale) + 'px';
      var cx = stateCv.getContext('2d');
      cx.setTransform(1, 0, 0, 1, 0, 0);
      cx.clearRect(0, 0, cw, ch);
      /* poster design units → this canvas */
      cx.setTransform(k, 0, 0, k, -(g.map.x + w.u0) * k, -(g.map.y + w.v0) * k);
      cx.imageSmoothingEnabled = true;
      cx.imageSmoothingQuality = 'high';
      cx.drawImage(cm.cv, pl.x, pl.y, pl.w, pl.h);
    }
    /* until the layer lands: the state's outline, faintly, where it will sit */
    function drawStateOutline() {
      var st = statesBy && statesBy[d.state];
      var w = look.win || { u0: 0, v0: 0, u1: g.map.w, v1: g.map.h };
      var uw = w.u1 - w.u0, vh = w.v1 - w.v0, k = Math.min(scale * (root.devicePixelRatio || 1), STATE_MAX_DIM / Math.max(uw, vh));
      var cw = Math.max(1, Math.round(uw * k)), ch = Math.max(1, Math.round(vh * k));
      stateCv.width = cw; stateCv.height = ch;
      stateCv.style.left = (w.u0 * scale) + 'px'; stateCv.style.top = (w.v0 * scale) + 'px';
      stateCv.style.width = (uw * scale) + 'px'; stateCv.style.height = (vh * scale) + 'px';
      var cx = stateCv.getContext('2d');
      cx.setTransform(1, 0, 0, 1, 0, 0);
      cx.clearRect(0, 0, cw, ch);
      if (!st) return;
      var fit = P.stateFit(g, st.aspect), t = P.theme(d.theme);
      cx.setTransform(k, 0, 0, k, -(g.map.x + w.u0) * k, -(g.map.y + w.v0) * k);
      cx.beginPath();
      st.rings.forEach(function (r) {
        r.forEach(function (pt, i) { cx[i ? 'lineTo' : 'moveTo'](fit.x + pt[0] / 1000 * fit.w, fit.y + pt[1] / 1000 * fit.h); });
        cx.closePath();
      });
      cx.fillStyle = t.road; cx.globalAlpha = 0.07; cx.fill();
      cx.globalAlpha = 0.35; cx.lineWidth = 0.6 / k * (root.devicePixelRatio || 1); cx.strokeStyle = t.road; cx.stroke();
      cx.globalAlpha = 1;
    }
    /* the maker opened on a state (a saved design, or ?state=): its words
       and place come from the table */
    function openState() {
      var code = d.state;
      states().then(function () {
        var st = statesBy[code];
        if (!st) { leaveState(true); return; }
        if (!saved || !saved.state) {
          d.title = st.name; d.sub = 'United States'; d.place = st.name + ', United States';
          d.lat = st.lat; d.lon = st.lon; d.orient = st.orient;
          d.tagAuto = true; d.tag = autoTag();
          home = homeHere();
        }
        d.place = d.place || st.name + ', United States';
        syncWords(); fontCards(); orientButtons(); renderOptions(); stateButtons();
        applyGeom(); drawOver(); renderState(); placeSoon(); saveSoon(); thumbs.request();
      }, function () { leaveState(true); });
    }

    /* ── Words ──────────────────────────────────────────────────────── */
    var wIn = { title: q('[data-w=title]'), sub: q('[data-w=sub]'), tag: q('[data-w=tag]'), tagAuto: q('[data-w=tagAuto]') };
    function syncWords(tagOnly) {
      if (!tagOnly) { wIn.title.value = d.title || ''; wIn.sub.value = d.sub || ''; wIn.tagAuto.setAttribute('aria-checked', d.tagAuto ? 'true' : 'false'); }
      wIn.tag.value = d.tag || '';
      wIn.tag.readOnly = !!d.tagAuto;
    }
    ['title', 'sub', 'tag'].forEach(function (k) {
      wIn[k].addEventListener('input', function () {
        d[k] = wIn[k].value;
        drawOver(); saveSoon(); thumbs.soon();
        if (k === 'title') { fontCards(); renderState(); }
        if (k !== 'tag' && !d.place) placeSoon();
        /* a script's face loads the characters it is asked for: new ones
           typed (東京 → 東京都) are fetched and the poster drawn again */
        if (P.font(d.font).local || P.scriptOf(d[k], d)) loadThen();
      });
    });
    wIn.tagAuto.addEventListener('click', function () {
      d.tagAuto = !d.tagAuto;
      if (d.tagAuto) d.tag = autoTag();
      syncWords(); drawOver(); saveSoon();
    });
    var fontsBox = q('.kpm-fonts');
    P.FONTS.forEach(function (f) {
      var s = f.title;
      fontsBox.appendChild(h('button', { type: 'button', 'data-font': f.key, class: 'kpm-fontcard', 'aria-pressed': 'false' },
        '<b style="font-family:\'' + s.family + '\';font-weight:' + s.weight + ';letter-spacing:' + (s.track * 0.6) + 'em;' +
        (s.caps ? 'text-transform:uppercase;' : '') + '"></b><span>' + esc(f.label) + '</span>'));
    });
    /* THE LOCAL CARD (KRPoster.LOCAL): there only for a place whose country
       writes in a script of its own (or a design lettered in one) — its
       face the place's own name in the script's face, its name the
       language. The five above stay as they are. */
    var localCard = h('button', { type: 'button', 'data-font': '', class: 'kpm-fontcard', 'aria-pressed': 'false', hidden: true }, '<b></b><span></span>');
    fontsBox.appendChild(localCard);
    function localOffer() {
      var f = P.font(d.font);
      if (f.local) return f;
      return d.local && !d.state ? P.localFor(d.local.cc) : null;
    }
    /* a card's face says what its lettering would put on the poster: the
       English name on a Latin card, the place's own spelling on the local
       one (its language's own name until that spelling is known) */
    function cardWord(f) {
      var t = d.title || 'Your city', lc = d.local;
      if (lc && lc.title && f.local && t === lc.en[0]) t = lc.title;
      if (lc && lc.title && !f.local && t === lc.title) t = lc.en[0] || t;
      if (f.local && !f.test.test(t)) t = f.native;
      return t.slice(0, 14);
    }
    function fontCards() {
      var L = localOffer();
      localCard.hidden = !L;
      if (L) {
        var s = L.title, face = localCard.querySelector('b');
        localCard.setAttribute('data-font', L.key);
        face.setAttribute('style', 'font-family:\'' + s.family + '\';font-weight:' + s.weight + ';letter-spacing:' + (s.track * 0.6) + 'em;' +
          (s.caps ? 'text-transform:uppercase;' : ''));
        face.setAttribute('lang', L.lang);
        face.setAttribute('dir', L.rtl ? 'rtl' : 'ltr');
        localCard.querySelector('span').textContent = L.label;
        P.localSheet(L);
      }
      qa('[data-font]', fontsBox).forEach(function (b) {
        var f = P.font(b.getAttribute('data-font'));
        if (!f) return;
        b.setAttribute('aria-pressed', f.key === d.font ? 'true' : 'false');
        b.querySelector('b').textContent = cardWord(f);
      });
      /* the fields take the script's language, so a phone offers its
         keyboard and the letters show in their own forms */
      var lang = P.font(d.font).local ? P.font(d.font).lang : '';
      ['title', 'sub'].forEach(function (k) { if (lang) wIn[k].setAttribute('lang', lang); else wIn[k].removeAttribute('lang'); });
    }
    /* switching between a Latin lettering and the local one swaps the words
       that are still the place's own (Tokyo ⇄ 東京, Japan ⇄ 日本); words the
       customer typed stay as typed */
    function toLocalWords() {
      var lc = d.local;
      if (!lc) return;
      if (lc.title && d.title === lc.en[0]) d.title = lc.title;
      if (d.sub === lc.en[1]) d.sub = lc.sub;
    }
    function toLatinWords() {
      var lc = d.local;
      if (!lc) return;
      if (lc.title && d.title === lc.title) d.title = lc.en[0];
      if (d.sub === lc.sub) d.sub = lc.en[1];
    }
    function setFont(key) {
      var was = P.font(d.font), next = P.font(key);
      if (!next) return;
      if (next.local && !was.local) toLocalWords();
      else if (!next.local && was.local) toLatinWords();
      if (!next.local) latinFont = next.key;
      d.font = next.key;
      fontBefore = null;   /* the customer's own pick stays, whatever the layout */
      syncWords(); fontCards(); renderState();
      loadThen();
      drawOver(); saveSoon(); thumbs.soon();
    }
    /* draw again once the faces the words call on are in */
    function loadThen() { P.loadFonts(d).then(function () { drawOver(); thumbs.soon(); fontCards(); }); }
    fontsBox.addEventListener('click', function (e) {
      var b = e.target.closest('[data-font]'); if (!b) return;
      setFont(b.getAttribute('data-font'));
    });

    /* ── Marker ─────────────────────────────────────────────────────── */
    var markersBox = q('.kpm-markers'), markHere = q('[data-mark-here]');
    var shape = function (key) {
      var m = P.MARKERS.filter(function (x) { return x.key === key; })[0];
      return m ? '<svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" d="' + m.path + '"/></svg>' : '';
    };
    markersBox.appendChild(h('button', { type: 'button', 'data-mark': '', class: 'kpm-markbtn' },
      '<svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true"><circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M6.5 17.5l11-11" stroke="currentColor" stroke-width="1.4"/></svg><span>None</span>'));
    P.MARKERS.forEach(function (m) {
      markersBox.appendChild(h('button', { type: 'button', 'data-mark': m.key, class: 'kpm-markbtn' }, shape(m.key) + '<span>' + esc(m.label) + '</span>'));
    });
    function markerButtons() {
      var cur = d.marker ? d.marker.icon : '';
      qa('[data-mark]', markersBox).forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-mark') === cur ? 'true' : 'false'); });
      markHere.hidden = !d.marker;
      if (d.marker) host.setAttribute('data-marked', ''); else host.removeAttribute('data-marked');
    }
    markersBox.addEventListener('click', function (e) {
      var b = e.target.closest('[data-mark]'); if (!b) return;
      var key = b.getAttribute('data-mark');
      /* a new marker goes on the spot at the middle of the map (the place
         just searched); changing the shape keeps it where it is */
      d.marker = key ? { icon: key, lat: d.marker ? d.marker.lat : d.lat, lon: d.marker ? d.marker.lon : d.lon } : null;
      if (d.tagAuto) { d.tag = autoTag(); syncWords(true); }
      markerButtons(); drawOver(); saveSoon(); thumbs.soon();
    });
    markHere.addEventListener('click', function () {
      if (!d.marker) return;
      d.marker = { icon: d.marker.icon, lat: d.lat, lon: d.lon };
      if (d.tagAuto) { d.tag = autoTag(); syncWords(true); }
      drawOver(); saveSoon(); thumbs.soon();
    });

    /* ── Style and Layout ───────────────────────────────────────────── */
    var themesBox = q('.kpm-themes'), layoutsBox = q('.kpm-layouts');
    var themeCanvas = {}, layoutCanvas = {};
    /* PRINT EFFECTS (KRPoster.FINISHES), an advanced option: below the
       colors, behind a door that stays shut until asked for (Kevin: "Only
       riso and film grain are worth keeping honestly and I think we need to
       keep them somewhere more like an advanced area"). A color set may
       carry its own (Mocha: film grain, Teal: risograph), so the shut door
       names the effect that's on; a pick here wins over the set's from
       then on, None included. advBox goes in after the color groups. */
    var fxBox = h('div', { class: 'kpm-group kpm-fx' }, '<span class="kpm-label">Print effect</span><div class="kpm-chips"></div>');
    P.FINISHES.forEach(function (F) {
      fxBox.lastChild.appendChild(h('button', { type: 'button', class: 'kpm-chip', 'data-fx': F.key, 'aria-pressed': 'false' }, esc(F.label)));
    });
    var advBody = h('div', { class: 'kpm-advbody', id: 'kpm-adv-' + uid, hidden: true });
    advBody.appendChild(fxBox);
    /* ELEVATION (Kevin, 2026-10-26: "How about adding mapterhorn elevation
       can I see what that would look like if turned on"): the hills shaded
       from the Mapterhorn archive the travel books use, over the land and
       under the streets. One switch, off by default; a whole state draws
       its own layer and has none. The section may leave the terrain URL
       blank, and then there is no switch at all. */
    var relBox = cfg.terrainUrl ? h('div', { class: 'kpm-group kpm-rel' }, '<span class="kpm-label">Elevation</span><div class="kpm-chips">' +
      '<button type="button" class="kpm-chip" data-rel="0" aria-pressed="true">Flat</button>' +
      '<button type="button" class="kpm-chip" data-rel="1" aria-pressed="false">Hills shaded</button></div>' +
      '<p class="kpm-tip kpm-reltip">' + icon(I.state, 20) + '<span>The land shades with its hills and valleys, from Mapterhorn’s elevation data. Flat cities stay much as they are; a city in the mountains gains its skyline.</span></p>') : null;
    if (relBox) advBody.appendChild(relBox);
    var advBtn = h('button', { type: 'button', class: 'kpm-advbtn', 'aria-expanded': 'false', 'aria-controls': 'kpm-adv-' + uid });
    var advBox = h('div', { class: 'kpm-adv' });
    advBox.appendChild(advBtn);
    advBox.appendChild(advBody);
    var CHEVRON = '<path d="M6 9l6 6 6-6"/>';
    function fxButtons() {
      var cur = P.finishOf(d), open = !advBody.hidden;
      qa('[data-fx]', fxBox).forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-fx') === cur.key ? 'true' : 'false'); });
      var rel = !!d.relief && !d.state;
      if (relBox) {
        relBox.hidden = !!d.state;
        qa('[data-rel]', relBox).forEach(function (b) { b.setAttribute('aria-pressed', (b.getAttribute('data-rel') === '1') === rel ? 'true' : 'false'); });
      }
      /* the shut door names what's on: "Film grain", "Hills shaded", or both */
      var on = [];
      if (cur.key !== 'none') on.push(cur.label);
      if (rel) on.push('Hills shaded');
      advBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      advBtn.innerHTML = '<span>' + (open ? 'Hide advanced options' : 'Show advanced options') + '</span>' +
        (!open && on.length ? '<span class="kpm-advnow">' + esc(on.join(' · ')) + '</span>' : '') + icon(CHEVRON, 16);
    }
    advBtn.addEventListener('click', function () { advBody.hidden = !advBody.hidden; fxButtons(); });
    if (relBox) relBox.addEventListener('click', function (e) {
      var b = e.target.closest('[data-rel]'); if (!b) return;
      var want = b.getAttribute('data-rel') === '1';
      if (want === !!d.relief) return;
      d.relief = want;
      fxButtons(); restyle(); drawOver(); saveSoon(); renderState();
      thumbs.request();
    });
    fxBox.addEventListener('click', function (e) {
      var b = e.target.closest('[data-fx]'); if (!b) return;
      d.finish = b.getAttribute('data-fx');
      fxButtons(); drawOver(); saveSoon(); renderState();
      thumbs.redrawThemes(); thumbs.redrawLayouts();
    });
    function posterCard(attr, key, label, paper) {
      var b = h('button', { type: 'button', class: 'kpm-card', 'aria-pressed': 'false' },
        '<span class="kpm-thumb"' + (paper ? ' style="background:' + paper + '"' : '') + '>' + TICK + '</span><span class="kpm-cardname">' + esc(label) + '</span>');
      b.setAttribute(attr, key);
      var cv = h('canvas', { 'aria-hidden': 'true' });
      var th = b.querySelector('.kpm-thumb');
      th.insertBefore(cv, th.firstChild);
      return { el: b, cv: cv };
    }
    P.THEME_GROUPS.forEach(function (gr) {
      var list = P.THEMES.filter(function (t) { return t.group === gr.id; });
      if (!list.length) return;
      var grp = h('div', { class: 'kpm-group' }, '<span class="kpm-label">' + esc(gr.label) + '</span>');
      var row = h('div', { class: 'kpm-cards' });
      list.forEach(function (t) {
        var c = posterCard('data-theme', t.key, t.label, t.paper);
        themeCanvas[t.key] = c.cv;
        row.appendChild(c.el);
      });
      grp.appendChild(row);
      themesBox.appendChild(grp);
    });
    themesBox.appendChild(advBox);
    P.LAYOUTS.forEach(function (L) {
      var c = posterCard('data-layout', L.key, L.label, null);
      layoutCanvas[L.key] = c.cv;
      layoutsBox.appendChild(c.el);
    });
    function styleCards() {
      qa('[data-theme]', themesBox).forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-theme') === d.theme ? 'true' : 'false'); });
      qa('[data-layout]', layoutsBox).forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-layout') === d.layout ? 'true' : 'false'); });
      fxButtons();
      paintThumb();
    }
    themesBox.addEventListener('click', function (e) {
      var b = e.target.closest('[data-theme]'); if (!b) return;
      d.theme = b.getAttribute('data-theme');
      /* a set that asks for the shaded hills (Trailhead) turns them on
         when it is picked; the Elevation chips still turn them off */
      var tt = P.theme(d.theme);
      if (tt && tt.relief && !d.relief && !d.state && cfg.terrainUrl) { d.relief = true; fxButtons(); thumbs.request(); }
      styleCards();
      restyle();
      applyGeom();
      saveSoon(); renderState();
      thumbs.redrawLayouts();
    });
    layoutsBox.addEventListener('click', function (e) {
      var b = e.target.closest('[data-layout]'); if (!b) return;
      var aim = P.aimLat(d);
      var relettered = layoutLetters(b.getAttribute('data-layout'));
      d.layout = b.getAttribute('data-layout');
      styleCards();
      applyGeom(); reaim(aim);
      if (relettered) { fontCards(); loadThen(); }
      saveSoon(); renderState();
      thumbs.redrawThemes();
    });

    /* the style cards are small posters of THIS map: one GL render in the
       class palette (KRPoster.CLASS_THEME), then every theme is mixed from
       it per pixel (KRPoster.recolor) — one map load for every card. A set
       with inks of its own for the side streets or paths (Night Lights:
       minor and pathInk, 2026-10-25) can't be mixed from the class render,
       which knows one road ink — mixed, its grey side streets came out
       amber and the card looked like Navy & Gold's. Such a set's card is a render
       of its own on the same map, taken after the class render (one extra
       GL pass per such set, no new tiles); until it lands the card shows
       the mixed stand-in. */
    var thumbs = (function () {
      var tmap = null, tbox = null, busy = false, again = false, key = '', classImg = null, ownImg = {}, reliefImg = null;
      /* the cards' tiles wait for the poster's coarse map (firstDrawn), so
         a slow connection brings the poster first; most of them are the
         same tiles, kept a moment by KRPoster's reader */
      var held = true;
      var soon = debounce(function () { redrawThemes(); redrawLayouts(); }, 150);
      function visible() { var t = host.getAttribute('data-tab'); return t === 'style' || t === 'layout'; }
      function ownInk(t) { return !!(t.minor || t.pathInk); }
      function go() { held = false; request(); }
      function request() {
        if (!visible()) return;
        if (d.state) { stateCards(); return; }
        /* (asked for again once the coarse map is up: firstDrawn) */
        if (held || !root.maplibregl || !root.pmtiles) return;
        var k = [d.lat.toFixed(4), d.lon.toFixed(4), d.zoom.toFixed(2), d.orient, d.size, d.buildings ? 1 : 0, d.lines, d.relief ? 1 : 0].join('|');
        if (k === key && classImg) { redrawThemes(); redrawLayouts(); return; }
        if (busy) { again = true; return; }
        busy = true; key = k; ownImg = {}; reliefImg = null;
        var base = Object.assign({}, d, { layout: 'classic' });
        var gg = P.geom(base);
        var tk = THUMB_W / gg.W;              /* device px per design unit on a card */
        var toz = 2;
        var w = Math.round(gg.map.w * tk), hh = Math.round(gg.map.h * tk);
        if (!tbox) {
          /* a zero-width space keeps the box from being an EMPTY div, which
             the theme hides — a hidden container gives MapLibre no size */
          tbox = h('div', { class: 'kpm-thumbmap', 'aria-hidden': 'true' }, '&#8203;');
          doc.body.appendChild(tbox);
        }
        tbox.style.width = (w * toz) + 'px';
        tbox.style.height = (hh * toz) + 'px';
        /* lines 2x their print weight: the card is half the canvas's size */
        /* demDrop: a card's elevation lines (Trailhead's) read two zoom
           levels coarser, a sixteenth of the elevation tiles */
        var styleFor = function (theme) { return P.style(base, { theme: theme, widthScale: tk * 2, detail: 1, pmtilesUrl: cfg.pmtilesUrl, relief: false, demDrop: 2 }); };
        var st = styleFor(P.CLASS_THEME);
        var zoom = d.zoom + Math.log(tk) / Math.LN2 + Math.log(toz) / Math.LN2;
        var grab = function () {
          var src = tmap.getCanvas();
          var c = doc.createElement('canvas'); c.width = w; c.height = hh;
          var cx = c.getContext('2d', { willReadFrequently: true });
          cx.drawImage(src, 0, 0, w, hh);
          return cx.getImageData(0, 0, w, hh);
        };
        /* the sets with inks of their own: one render each, after the class
           render, each card redrawn as its render lands */
        var own = P.THEMES.filter(ownInk);
        /* the hills, once, neutral: shadow and light on mid grey, laid over
           every card with a soft-light blend (reliefCard), so no card needs
           a render of its own for them */
        var wantRelief = !!d.relief && !!cfg.terrainUrl;
        var next = function () {
          var t = own.shift();
          if (!t) {
            if (wantRelief && !reliefImg) {
              tmap.setStyle(P.reliefStyle(base, { terrainUrl: cfg.terrainUrl, oz: toz * tk }));
              tmap.triggerRepaint();
              tmap.once('idle', function () {
                try { reliefImg = grab(); } catch (e) { reliefImg = null; }
                redrawThemes(); redrawLayouts();
                busy = false; if (again) { again = false; request(); }
              });
              return;
            }
            busy = false; if (again) { again = false; request(); } return;
          }
          tmap.setStyle(styleFor(t));
          tmap.triggerRepaint();
          tmap.once('idle', function () {
            try { ownImg[t.key] = grab(); } catch (e) { /* the stand-in stays */ }
            card(themeCanvas[t.key], Object.assign({}, d, { theme: t.key }), t);
            if (d.theme === t.key) redrawLayouts();
            next();
          });
        };
        var go = function () {
          tmap.once('idle', function () {
            try { classImg = grab(); } catch (e) { classImg = null; }
            redrawThemes(); redrawLayouts();
            next();
          });
        };
        if (!tmap) {
          tmap = new root.maplibregl.Map({ container: tbox, style: st, center: [d.lon, d.lat], zoom: zoom, interactive: false,
            attributionControl: false, fadeDuration: 0, pixelRatio: 1 / toz, renderWorldCopies: false,
            canvasContextAttributes: { preserveDrawingBuffer: true, antialias: true } });
          go();
        } else {
          /* new style (streets, buildings and widths change with the design),
             size and camera, then a forced repaint: 'idle' only fires after
             a render, and an unchanged camera would leave the cards waiting */
          tmap.setStyle(st);
          tmap.resize();
          tmap.jumpTo({ center: [d.lon, d.lat], zoom: zoom });
          tmap.triggerRepaint();
          go();
        }
      }
      /* one card: paper, the map (the set's own render, else the class
         render recolored) into the layout's map rect (cover-fit, clipped to
         its shape), then the frame and words */
      function card(cv, dd, theme) {
        if (!classImg) return;
        var img = ownImg[theme.key] || null;
        var gg = P.geom(dd), tk = THUMB_W / gg.W;
        cv.width = Math.round(gg.W * tk); cv.height = Math.round(gg.H * tk);
        cv.__drawn = true;
        cv.parentNode.style.aspectRatio = gg.W + ' / ' + gg.H;
        var cx = cv.getContext('2d');
        cx.fillStyle = theme.paper; cx.fillRect(0, 0, cv.width, cv.height);
        var tmp = doc.createElement('canvas'); tmp.width = classImg.width; tmp.height = classImg.height;
        var tcx = tmp.getContext('2d');
        tcx.putImageData(img || P.recolor(classImg, theme, tcx.createImageData(classImg.width, classImg.height)), 0, 0);
        var rx = gg.map.x * tk, ry = gg.map.y * tk, rw = gg.map.w * tk, rh = gg.map.h * tk;
        var s = Math.max(rw / tmp.width, rh / tmp.height);
        cx.save(); P.clipMap(cx, gg, tk);
        cx.drawImage(tmp, rx + (rw - tmp.width * s) / 2, ry + (rh - tmp.height * s) / 2, tmp.width * s, tmp.height * s);
        if (reliefImg && dd.relief && !dd.state) {
          /* the neutral hills over the map: soft-light leaves mid grey
             alone, darkens under the shadows, lightens under the light */
          var rc = doc.createElement('canvas'); rc.width = reliefImg.width; rc.height = reliefImg.height;
          rc.getContext('2d').putImageData(reliefImg, 0, 0);
          cx.globalCompositeOperation = 'soft-light';
          cx.globalAlpha = 0.9;
          cx.drawImage(rc, rx + (rw - rc.width * s) / 2, ry + (rh - rc.height * s) / 2, rc.width * s, rc.height * s);
          cx.globalAlpha = 1;
          cx.globalCompositeOperation = 'source-over';
        }
        cx.restore();
        cardOverlay(cx, dd, tk, gg, theme);
      }
      /* the overlay and the finish on a card as on the poster: a
         risograph's plate off register (under a pixel at a card's scale:
         a tint on the words and the frame, as the print has from across a
         room), then the layers, where the grain averages away and the
         paper shows */
      function cardOverlay(cx, dd, tk, gg, theme) {
        var f = P.finishOf(dd, theme);
        if (f.misreg) {
          P.drawOverlay(cx, dd, tk, gg, { theme: theme, noCredit: true, noPlate: true });
          P.drawPlateOff(cx, dd, tk, gg, { theme: theme }, f.misreg.dx * tk, f.misreg.dy * tk);
        } else P.drawOverlay(cx, dd, tk, gg, { theme: theme, noCredit: true });
        if (f.layers.length) P.drawFinish(cx, dd, tk, gg, { theme: theme, finish: f });
      }
      function redrawThemes() {
        if (d.state) { stateCards(); return; }
        if (!classImg) return;
        P.THEMES.forEach(function (t) { card(themeCanvas[t.key], Object.assign({}, d, { theme: t.key }), t); });
      }
      function redrawLayouts() {
        if (d.state) return;   /* stateCards draws both */
        if (!classImg) return;
        var t = P.theme(d.theme);
        P.LAYOUTS.forEach(function (L) { card(layoutCanvas[L.key], Object.assign({}, d, { layout: L.key, font: layoutFont(L.key) }), t); });
        paintThumb();
      }
      /* A STATE'S CARDS: the layer shrunk to card size once (its channels
         are linear shares, so a bilinear shrink keeps them true), then each
         card recolors that small copy — no map render at all. The dark-line
         class's cards come from the layer on hand: at card size the two
         classes draw the same. */
      var small = { key: '', cls: null, mask: null, meta: null };
      function shrink(img, w, hh) {
        var a = doc.createElement('canvas'); a.width = img.width; a.height = img.height;
        a.getContext('2d').putImageData(img, 0, 0);
        var b = doc.createElement('canvas'); b.width = w; b.height = hh;
        var bx = b.getContext('2d', { willReadFrequently: true });
        bx.imageSmoothingEnabled = true; bx.imageSmoothingQuality = 'high';
        bx.drawImage(a, 0, 0, w, hh);
        return bx.getImageData(0, 0, w, hh);
      }
      function stateCards() {
        var lay = layerReady();
        if (!lay) { stateLayer().then(function () { if (visible() && d.state) stateCards(); }, function () {}); return; }
        var key = layerKey();
        if (small.key !== key) {
          var f = Math.min(1, 420 / Math.max(lay.cls.width, lay.cls.height));
          var w = Math.max(1, Math.round(lay.cls.width * f)), hh = Math.max(1, Math.round(lay.cls.height * f));
          var sp = lay.meta.state_px;
          small = { key: key, cls: shrink(lay.cls, w, hh), mask: shrink(lay.mask, w, hh), baked: lay.meta.hair_alpha,
            meta: { image_in: { px_w: w, px_h: hh }, state_px: { left: sp.left * f, top: sp.top * f, w: sp.w * f, h: sp.h * f } } };
        }
        P.THEMES.forEach(function (t) { stateCard(themeCanvas[t.key], Object.assign({}, d, { theme: t.key }), t); });
        var t0 = P.theme(d.theme);
        P.LAYOUTS.forEach(function (L) { stateCard(layoutCanvas[L.key], Object.assign({}, d, { layout: L.key, font: layoutFont(L.key) }), t0); });
        paintThumb();
      }
      function stateCard(cv, dd, theme) {
        var gg = P.geom(dd), tk = THUMB_W / gg.W;
        cv.width = Math.round(gg.W * tk); cv.height = Math.round(gg.H * tk);
        cv.__drawn = true;
        cv.parentNode.style.aspectRatio = gg.W + ' / ' + gg.H;
        var cx = cv.getContext('2d');
        cx.fillStyle = theme.paper; cx.fillRect(0, 0, cv.width, cv.height);
        var tmp = doc.createElement('canvas'); tmp.width = small.cls.width; tmp.height = small.cls.height;
        var tcx = tmp.getContext('2d');
        tcx.putImageData(P.stateRecolor(small.cls, small.mask, theme, { share: dd.share, baked: small.baked }, tcx.createImageData(tmp.width, tmp.height)), 0, 0);
        var pl = P.statePlace(gg, small.meta);
        cx.save(); P.clipMap(cx, gg, tk);
        cx.drawImage(tmp, pl.x * tk, pl.y * tk, pl.w * tk, pl.h * tk);
        cx.restore();
        cardOverlay(cx, dd, tk, gg, theme);
      }
      return { request: request, go: go, soon: soon, redrawThemes: redrawThemes, redrawLayouts: redrawLayouts };
    })();

    /* ── Size & finish ──────────────────────────────────────────────── */
    var groups = q('.kpm-optgroups');
    function optLabel(name) { return /^style$/i.test(name) ? 'Finish' : name; }
    function low(list) {
      var a = list.filter(function (v) { return v.available; });
      return (a.length ? a : list).reduce(function (m, v) { return Math.min(m, v.price); }, Infinity);
    }
    function cheapest(list) {
      var a = list.filter(function (v) { return v.available; });
      return (a.length ? a : list).slice().sort(function (x, y) { return x.price - y.price; })[0] || null;
    }
    function priceLine(list) {
      if (!list.some(function (v) { return v.available; })) return 'Sold out';
      var lo = low(list);
      return isFinite(lo) ? (list.length > 1 ? 'from ' : '') + fmtMoney(lo) : '';
    }
    /* a size value as the customer sees it on the wall: "16 × 20 in" in
       portrait, "20 × 16 in" turned */
    function sizeText(val) {
      var s = sizeOf(val); if (!s) return val;
      var i = P.sizeInches(s, d.orient);
      return trimNum(i.w) + ' × ' + trimNum(i.h) + ' in';
    }
    function finishText(val) {
      var t = String(val || '').replace(/\s+frame$/i, '');
      return t && !/^no$/i.test(t) ? t : String(val || '');
    }
    function frameKey(txt) {
      txt = String(txt || '').toLowerCase();
      if (/no.?frame|unframed|without frame/.test(txt) || !/frame|framed|oak|walnut/.test(txt)) return '';
      /* Kevin's own mouldings first (the desk-size maps' frames): white oak
         is a honey oak, not white paint */
      if (/white.?oak/.test(txt)) return 'whiteoak';
      if (/dark.?oak/.test(txt)) return 'darkoak';
      if (/black/.test(txt)) return 'black';
      if (/white/.test(txt)) return 'white';
      if (/walnut|dark wood/.test(txt)) return 'walnut';
      return 'natural';
    }
    /* the variants that match the choices made before option i (the
       generic groups only offer what exists with the earlier choices) */
    function matching(i, choice) {
      return (product.variants || []).filter(function (v) {
        var o = vo(v);
        for (var j = 0; j < i; j++) if (o[j] !== choice[j]) return false;
        return true;
      });
    }
    /* after option i changes, any later option that no longer exists with it
       moves to the first value that does */
    function settle(i) {
      for (var j = i + 1; j < opts.length; j++) {
        var ok = matching(j, sel).map(function (v) { return vo(v)[j]; });
        if (ok.indexOf(sel[j]) < 0 && ok.length) sel[j] = ok[0];
      }
    }
    function renderOptions() {
      if (!structured) { renderGeneric(); return; }
      var all = product.variants || [];
      var fmt = variant ? formatOf(variant) : (formats[0] && formats[0].key);
      var html = '';
      if (formats.length > 1) {
        html += '<div class="kpm-group"><span class="kpm-label">Format</span><div class="kpm-opts' + (formats.length === 3 ? ' kpm-opts3' : '') + '">';
        formats.forEach(function (f) {
          var list = all.filter(function (v) { return formatOf(v) === f.key; });
          var any = list.some(function (v) { return v.available; });
          html += '<button type="button" class="kpm-opt kpm-optc" data-format="' + f.key + '" aria-pressed="' + (f.key === fmt) + '"' + (any ? '' : ' disabled') + '>' +
            icon(I[f.key], 22) + '<b>' + f.label + '</b><small>' + esc(priceLine(list)) + '</small></button>';
        });
        html += '</div></div>';
      }
      var inFmt = all.filter(function (v) { return formatOf(v) === fmt; });
      if (sizeIdx >= 0) {
        var sizes = uniq(inFmt.map(function (v) { return vo(v)[sizeIdx]; }));
        if (sizes.length) {
          html += '<div class="kpm-group"><span class="kpm-label">Size</span><div class="kpm-opts">';
          sizes.forEach(function (sv) {
            var list = inFmt.filter(function (v) { return vo(v)[sizeIdx] === sv; });
            var any = list.some(function (v) { return v.available; });
            html += '<button type="button" class="kpm-opt" data-size="' + esc(sv) + '" aria-pressed="' + (sel[sizeIdx] === sv) + '"' + (any ? '' : ' disabled') + '>' +
              '<b>' + esc(sizeText(sv)) + '</b><small>' + esc(priceLine(list)) + '</small></button>';
          });
          html += '</div></div>';
        }
      }
      if (finishIdx >= 0) {
        var here = inFmt.filter(function (v) { return sizeIdx < 0 || vo(v)[sizeIdx] === sel[sizeIdx]; });
        var fins = uniq(here.map(function (v) { return vo(v)[finishIdx]; }));
        if (fins.length > 1) {
          html += '<div class="kpm-group"><span class="kpm-label">' + (fmt === 'framed' ? 'Frame' : 'Finish') + '</span><div class="kpm-opts">';
          fins.forEach(function (fv) {
            var v = here.filter(function (x) { return vo(x)[finishIdx] === fv; })[0];
            var wood = fmt === 'framed' ? frameKey(fv) : '';
            html += '<button type="button" class="kpm-opt' + (wood ? ' kpm-optwood' : '') + '" data-finish="' + esc(fv) + '" aria-pressed="' + (sel[finishIdx] === fv) + '"' + (v && v.available ? '' : ' disabled') + '>' +
              (wood ? '<span class="kpm-swatch" style="--kpm-wood:' + WOOD[wood] + (GRAIN[wood] != null ? ';--kpm-grain:' + GRAIN[wood] : '') + '">' + grainSvg(gh) + '</span>' : '') +
              '<b>' + esc(finishText(fv)) + '</b><small>' + (v && v.available ? esc(fmtMoney(v.price)) : 'Sold out') + '</small></button>';
          });
          html += '</div></div>';
        }
      }
      groups.innerHTML = html;
    }
    /* any other product: one plain group per option, each offering only
       what exists with the choices before it */
    function renderGeneric() {
      var html = '';
      opts.forEach(function (name, i) {
        var pool = matching(i, sel), values = uniq(pool.map(function (v) { return vo(v)[i]; }));
        if (values.length < 2 && /^title$/i.test(name)) return;
        html += '<div class="kpm-group"><span class="kpm-label">' + esc(optLabel(name)) + '</span><div class="kpm-opts">';
        values.forEach(function (val) {
          var list = pool.filter(function (v) { return vo(v)[i] === val; });
          var any = list.some(function (v) { return v.available; });
          var later = i < opts.length - 1 && list.length > 1;
          var lo = low(list);
          html += '<button type="button" class="kpm-opt" data-opt="' + i + '" data-val="' + esc(val) + '" aria-pressed="' + (sel[i] === val) + '"' + (any ? '' : ' disabled') + '>' +
            '<b>' + esc(i === sizeIdx ? sizeText(val) : val) + '</b><small>' + (any ? esc(isFinite(lo) ? (later ? 'from ' : '') + fmtMoney(lo) : '') : 'Sold out') + '</small></button>';
        });
        html += '</div></div>';
      });
      groups.innerHTML = html;
    }
    groups.addEventListener('click', function (e) {
      var b = e.target.closest('button'); if (!b || b.disabled) return;
      var all = product.variants || [], cur = variant, fmt = cur ? formatOf(cur) : null, next = null;
      if (b.hasAttribute('data-format')) {
        /* a format: the same size in it if it comes in that size, else its
           cheapest */
        var f = b.getAttribute('data-format');
        var inF = all.filter(function (v) { return formatOf(v) === f; });
        var same = sizeIdx >= 0 && cur ? inF.filter(function (v) { return vo(v)[sizeIdx] === vo(cur)[sizeIdx]; }) : [];
        next = cheapest(same.length ? same : inF);
      } else if (b.hasAttribute('data-size')) {
        /* a size: the same frame or finish in it if there is one */
        var sv = b.getAttribute('data-size');
        var inS = all.filter(function (v) { return formatOf(v) === fmt && vo(v)[sizeIdx] === sv; });
        var keep = finishIdx >= 0 && cur ? inS.filter(function (v) { return vo(v)[finishIdx] === vo(cur)[finishIdx]; }) : [];
        next = cheapest(keep.length ? keep : inS);
      } else if (b.hasAttribute('data-finish')) {
        var fv = b.getAttribute('data-finish');
        next = all.filter(function (v) {
          return formatOf(v) === fmt && (sizeIdx < 0 || vo(v)[sizeIdx] === sel[sizeIdx]) && vo(v)[finishIdx] === fv;
        })[0] || null;
      } else if (b.hasAttribute('data-opt')) {
        var i = +b.getAttribute('data-opt');
        sel[i] = b.getAttribute('data-val');
        settle(i);
        next = findVariant(sel) || variant;
      }
      if (next) setVariant(next);
    });
    function setVariant(v) {
      var oldW = g.map.w;
      variant = v;
      sel = vo(v).slice(0, opts.length);
      var ns = sizeIdx >= 0 ? sizeOf(sel[sizeIdx]) : null;
      if (ns && ns !== d.size) {
        var aim = P.aimLat(d);
        d.size = ns;
        var nw = P.geom(d).map.w;
        var dz = Math.log(nw / oldW) / Math.LN2;   /* same ground, bigger paper */
        d.zoom += dz; home.zoom += dz;
        applyGeom();
        if (map && !d.state) map.jumpTo({ zoom: P.camZoom(d, oz) });
        reaim(aim);
      }
      renderOptions(); renderPrice(); applyFrame(); saveSoon();
    }
    qa('[data-orient]', paneSize).forEach(function (b) {
      b.addEventListener('click', function () {
        var aim = P.aimLat(d);
        d.orient = b.getAttribute('data-orient');
        orientButtons();
        applyGeom(); reaim(aim); renderOptions(); renderState(); saveSoon(); thumbs.request();
      });
    });
    function orientButtons() {
      qa('[data-orient]', paneSize).forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-orient') === d.orient ? 'true' : 'false'); });
      paintThumb();
    }

    /* the chosen variant's frame, drawn around the preview: any option
       value naming a frame and a colour ("Dark Oak Frame", "Black frame",
       "Natural wood frame") gets one; anything else shows the bare print */
    function frameOf() { return frameKey(sel.join(' ')); }
    /* what the print is mounted as, for the preview's edge and shadow */
    function mountOf() {
      var txt = sel.join(' ').toLowerCase();
      if (/canvas/.test(txt)) return 'canvas';
      if (/foam|mounted|board/.test(txt)) return 'foam';
      return '';
    }
    function applyFrame() {
      var fr = frameOf(), mt = mountOf();
      if (fr) host.setAttribute('data-frame', fr); else host.removeAttribute('data-frame');
      if (mt && !fr) host.setAttribute('data-mount', mt); else host.removeAttribute('data-mount');
      fitStage();
    }

    /* ── what is being made: the bar, the buy bar, the summary ──────── */
    function finishLabel() {
      if (!variant) return '';
      if (!structured || finishIdx < 0) {
        var parts = [];
        opts.forEach(function (n, i) { if (!/^title$/i.test(n) && i !== sizeIdx && sel[i]) parts.push(sel[i]); });
        return parts.join(' · ');
      }
      var fv = vo(variant)[finishIdx] || '', f = formatOf(variant);
      if (f === 'framed') return finishText(fv) + ' frame';
      if (f === 'poster' && /^print$/i.test(fv)) return 'Poster print';
      return fv;
    }
    function renderState() {
      var t = P.theme(d.theme) || {};
      var L = P.LAYOUTS.filter(function (x) { return x.key === d.layout; })[0] || P.LAYOUTS[0];
      var what = [sizeLabel(), finishLabel()].filter(Boolean).join(' · ');
      stateEl.textContent = [d.title || 'Your map', t.label, what].filter(Boolean).join(' · ');
      buyWhat.textContent = what;
      sizePill.textContent = sizeLabel();
      sumText.textContent = [d.state ? 'Every road in ' + d.title : placeName(), t.label, L.label + ' layout',
        sizeLabel() + ', ' + d.orient, finishLabel()].filter(Boolean).join(' · ');
    }

    /* ── price + cart ───────────────────────────────────────────────── */
    var busyCart = false;
    function renderPrice() {
      var html = '';
      if (variant) {
        var cmp = variant.compare_at_price && variant.compare_at_price > variant.price
          ? '<s>' + esc(fmtMoney(variant.compare_at_price)) + '</s>' : '';
        html = cmp + '<b>' + esc(fmtMoney(variant.price)) + '</b>';
      }
      qa('.kpm-price').forEach(function (el) { el.innerHTML = html; });
      var can = !!(variant && variant.available);
      qa('.kpm-add').forEach(function (b) {
        b.disabled = !can || busyCart;
        var lab = b.querySelector('.kpm-addlabel');
        if (lab) lab.textContent = !variant || can ? 'Add to cart' : 'Sold out';
      });
      qa('.kpm-addprice').forEach(function (el) { el.textContent = variant && can ? ' · ' + fmtMoney(variant.price) : ''; });
      renderState();
    }
    var toastT = null;
    function toast(text, ms) {
      clearTimeout(toastT);
      toastEl.textContent = text || '';
      toastEl.hidden = !text;
      if (text && ms) toastT = setTimeout(function () { toastEl.hidden = true; }, ms);
    }
    function properties() {
      var t = P.theme(d.theme), f = P.font(d.font) || P.FONTS[0];
      var L = P.LAYOUTS.filter(function (x) { return x.key === d.layout; })[0] || P.LAYOUTS[0];
      var inch = P.sizeInches(d.size, d.orient);
      var code = P.encode(d);
      var props = {};
      /* a whole state: the cart line names it as such; the print tool reads
         the state, form, share and ladder from the design in the link */
      if (d.state) { props['Place'] = 'The state of ' + d.title; props['Map'] = 'Every road in ' + d.title; }
      else if (d.place) props['Place'] = d.place;
      if (d.title) props['Title'] = d.title;
      if (d.sub) props['Second line'] = d.sub;
      if (d.tag) props['Third line'] = d.tag;
      props['Colors'] = t.label;
      var fin = P.finishOf(d);
      if (fin.key !== 'none') props['Effect'] = fin.label;
      if (d.relief && !d.state) props['Elevation'] = 'Hills shaded';
      props['Layout'] = L.label;
      props['Lettering'] = f.label;
      props['Poster'] = trimNum(inch.w) + ' x ' + trimNum(inch.h) + ' in, ' + (d.orient === 'landscape' ? 'landscape' : 'portrait');
      if (d.marker) {
        var mdef = P.MARKERS.filter(function (x) { return x.key === d.marker.icon; })[0];
        props['Marker'] = (mdef ? mdef.label : d.marker.icon) + ' at ' + P.fmtCoords(d.marker.lat, d.marker.lon);
      }
      props['_design'] = code;
      /* the print link: Kevin's print tool reads the design from the hash
         (the variant rides along to name the ordered finish) */
      props['_print_file'] = root.location.origin + root.location.pathname + '?print=1' + (variant ? '&variant=' + variant.id : '') + '#d=' + code;
      return props;
    }
    host.addEventListener('click', function (e) {
      var btn = e.target.closest('.kpm-add');
      if (btn && host.contains(btn) && !btn.disabled) addToCart(btn);
    });
    function addToCart(btn) {
      if (!variant || busyCart) return;
      busyCart = true;
      renderPrice();
      toast('');
      var cart = doc.querySelector('cart-notification') || doc.querySelector('cart-drawer');
      var fd = new FormData();
      fd.append('id', variant.id);
      fd.append('quantity', 1);
      var props = properties();
      Object.keys(props).forEach(function (k) { fd.append('properties[' + k + ']', props[k]); });
      if (cart && cart.getSectionsToRender) {
        fd.append('sections', cart.getSectionsToRender().map(function (s) { return s.id; }));
        fd.append('sections_url', root.location.pathname);
        if (cart.setActiveElement) cart.setActiveElement(btn);
      }
      var done = function () { busyCart = false; renderPrice(); };
      fetch(cfg.cartAddUrl || '/cart/add.js', { method: 'POST', body: fd, headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' } })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          done();
          if (res && res.status) { toast(res.description || res.message || 'Could not add to cart.'); return; }
          if (cart && cart.renderContents && res.sections) { cart.renderContents(res); if (cart.classList) cart.classList.remove('is-empty'); }
          else root.location.href = cfg.cartUrl || '/cart';
        })
        .catch(function () { done(); toast('Could not add to cart. Please try again.'); });
    }

    /* ── Share: the design lives in this page's link ────────────────── */
    var shareBtn = q('.kpm-share'), shareLabel = shareBtn.querySelector('span'), shareT = null;
    function copyText(text) {
      if (root.navigator.clipboard && root.navigator.clipboard.writeText) return root.navigator.clipboard.writeText(text);
      return new Promise(function (ok, no) {
        var ta = h('textarea', { 'aria-hidden': 'true', style: 'position:fixed;left:-9999px;top:0' });
        ta.value = text; doc.body.appendChild(ta); ta.select();
        var good = false; try { good = doc.execCommand('copy'); } catch (e) {}
        ta.remove(); if (good) ok(); else no();
      });
    }
    function share() {
      saveNow();
      var url = root.location.href;
      if (isTouch() && root.navigator.share) {
        root.navigator.share({ title: (d.title || 'My map') + ' · ' + (product.title || 'KR Maps'), url: url }).catch(function () {});
        return;
      }
      copyText(url).then(function () {
        clearTimeout(shareT);
        shareLabel.textContent = 'Link copied';
        if (!shareLabel.offsetParent) toast('Link copied', 2000);
        shareT = setTimeout(function () { shareLabel.textContent = 'Share'; }, 2000);
      }, function () { toast('Copy this page’s address to share your design.', 4000); });
    }
    shareBtn.addEventListener('click', share);
    q('.kpm-stageshare', tools).addEventListener('click', share);

    /* ── keep the design in the URL (refresh and share keep it) ─────── */
    /* the variant goes in the query (Shopify's own ?variant=), the design in
       the hash: a refreshed or shared link keeps the size and frame too. A
       link's opening (?color=, ?city=… — linkOpening) is in the hash from
       here on, so it leaves the query. */
    function saveNow() {
      try {
        var qs = root.location.search.replace(/^\?/, '').split('&').filter(function (p) { return p && !/^variant=/.test(p) && !OPENING_PARAMS.test(p); });
        if (variant) qs.push('variant=' + variant.id);
        root.history.replaceState(root.history.state, '', root.location.pathname + (qs.length ? '?' + qs.join('&') : '') + '#d=' + P.encode(d));
      } catch (e) {}
    }
    var saveSoon = debounce(saveNow, 400);

    /* desktop: the editor fills the screen under the site header */
    function sizeHost() {
      /* a phone's editor is part of the page, as long as its step (the
         page opens at it with the site header just above: the section's
         inline script) */
      if (isPhone()) { host.style.removeProperty('--kpm-h'); return; }
      var top = host.getBoundingClientRect().top + (root.scrollY || 0);
      host.style.setProperty('--kpm-h', Math.max(560, root.innerHeight - Math.min(top, 180)) + 'px');
    }
    sizeHost();
    root.addEventListener('resize', debounce(sizeHost, 150));

    /* ── phone or desktop ───────────────────────────────────────────── */
    /* the words that differ (the Look step, its head) */
    function applyMode() {
      phoneMode = isPhone();
      qa('[data-ph]', body).forEach(function (el) { el.textContent = el.getAttribute(phoneMode ? 'data-ph' : 'data-dk'); });
      var lb = q('[data-tab="style"] .kpm-tablabel', rail);
      if (lb) lb.textContent = phoneMode ? 'Look' : 'Style';
    }
    /* turned past 900px (a tablet rotated, a window resized): the steps
       regroup where they are, without moving the page */
    function onMode() {
      var was = phoneMode;
      applyMode();
      if (was === phoneMode) return;
      showTab(host.getAttribute('data-tab'), true);
      setStuck(stage.getBoundingClientRect().bottom <= 0);
    }
    var mq = root.matchMedia ? root.matchMedia('(max-width: 899px)') : null;
    if (mq) { if (mq.addEventListener) mq.addEventListener('change', onMode); else if (mq.addListener) mq.addListener(onMode); }

    /* THE STRIP'S THUMB (phone): shown once the poster has scrolled off
       the top, the strip stuck there in its place. It is the selected
       Layout card (this poster in its colors, words and marker), copied
       whenever the cards redraw; until the Look step has drawn them, a
       poster icon. */
    var stuck = false;
    function setStuck(on) {
      on = !!on && phoneMode;
      if (on === stuck) return;
      stuck = on;
      if (on) host.setAttribute('data-stuck', ''); else host.removeAttribute('data-stuck');
      railThumb.tabIndex = on ? 0 : -1;
      if (on) paintThumb();
    }
    function paintThumb() {
      if (!phoneMode) return;
      var src = layoutCanvas[d.layout];
      /* a card drawn before the poster changed shape (turned landscape on
         the Size step: the cards wait for the Look step to redraw) would
         show the wrong poster; the icon stands in until they catch up */
      var g0 = P.geom(d);
      var same = src && src.width && src.height && Math.abs(src.width / src.height - g0.W / g0.H) < 0.02;
      if (!src || !src.__drawn || !same) { railCv.hidden = true; railThumb.removeAttribute('data-drawn'); return; }
      /* 46px tall, or 56px wide for a landscape poster (the thumb's box) */
      var ar = src.width / src.height, hh = 46, w = Math.round(hh * ar);
      if (w > 56) { w = 56; hh = Math.round(w / ar); }
      var dpr = Math.min(3, root.devicePixelRatio || 1);
      railCv.width = Math.round(w * dpr); railCv.height = Math.round(hh * dpr);
      railCv.style.width = w + 'px'; railCv.style.height = hh + 'px';
      var cx = railCv.getContext('2d');
      cx.imageSmoothingEnabled = true; cx.imageSmoothingQuality = 'high';
      cx.drawImage(src, 0, 0, railCv.width, railCv.height);
      railCv.hidden = false;
      railThumb.setAttribute('data-drawn', '');
    }
    railThumb.addEventListener('click', function () {
      var top = stage.getBoundingClientRect().top + (root.scrollY || 0);
      try { root.scrollTo({ top: top, behavior: 'smooth' }); } catch (e) { root.scrollTo(0, top); }
    });
    /* the strip sticks exactly when the poster's bottom (where the strip
       sits in the page) reaches the top of the screen. The margin: a box
       whose edge only touches the root still counts as intersecting, and
       a step change scrolls to exactly that point (showTab) */
    if (root.IntersectionObserver) {
      new IntersectionObserver(function (es) {
        var e = es[es.length - 1];
        setStuck(!e.isIntersecting && e.boundingClientRect.bottom <= 1);
      }, { rootMargin: '-1px 0px 0px 0px' }).observe(stage);
    }

    /* ── start ──────────────────────────────────────────────────────── */
    syncWords(); fontCards(); styleCards(); orientButtons(); renderOptions(); markerButtons(); chipsState();
    var fr0 = frameOf(), mt0 = mountOf();
    if (fr0) host.setAttribute('data-frame', fr0); else if (mt0) host.setAttribute('data-mount', mt0);
    applyGeom();
    applyMode();
    /* a phone opens on the Look step, the colors and layouts (Kevin,
       10-27: "the tool should start off with the style page selected");
       the place is already on the poster and one tap away. A whole state's
       link opens on Place, where its list of states is the point. A
       desktop opens on Place, step 1 of its six. */
    showTab(phoneMode && !d.state ? 'style' : 'place', true);
    if (root.ResizeObserver) new ResizeObserver(function () { fitStage(); }).observe(stage);
    else root.addEventListener('resize', fitStage);
    P.loadFonts(d).then(drawOver);
    if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(drawOver);
    /* a whole state needs no map until the door back to a city is opened */
    if (d.state) { enterStateMode(); showPicker(true); openState(); }
    else { readFirstEarly(); startMap(); }
    /* a link that named a city alone: look it up and open on it (openPlace) */
    if (findPlace) openPlace(findPlace);
    saveSoon();

    self.design = function () { return Object.assign({}, d); };
    self.map = function () { return map; };
    self.variant = function () { return variant; };
    self.properties = properties;
    self.showTab = showTab;
  }

  function mountAll() {
    Array.prototype.forEach.call(doc.querySelectorAll('[data-kpm]'), function (host) {
      if (host.__kpm) return;
      var cfgEl = doc.getElementById(host.getAttribute('data-kpm'));
      var cfg = {};
      try { cfg = JSON.parse(cfgEl ? cfgEl.textContent : '{}'); } catch (e) {}
      if (!root.KRPoster) return;
      host.__kpm = new Maker(host, cfg);
    });
  }
  root.KRPosterMaker = { mount: mountAll, Maker: Maker, pngWithDpi: pngWithDpi };
  /* the section runs this file after its host and config, while the page
     may still be loading: mount now, and once more at DOMContentLoaded for
     a host further down */
  mountAll();
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', mountAll);
  /* the theme editor re-renders a section without re-running its scripts;
     the libraries stay on window from the first load, so mount again */
  doc.addEventListener('shopify:section:load', mountAll);
})(window);
